import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, where, addDoc, updateDoc, doc, serverTimestamp, orderBy, arrayUnion, deleteDoc, getDocs, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
    Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Line, ComposedChart, LineChart
} from 'recharts';
import {
    Users, TrendingUp, CheckSquare, RefreshCw, CreditCard, DollarSign, Clock, PlayCircle, StopCircle, MessageSquare, Send, AlertTriangle, CheckCircle, CalendarDays, Megaphone, Trash2, Edit
} from 'lucide-react';
import { useDashboardData } from '../hooks/useDashboardData';
import { useAuth } from '../hooks/useAuth';
import Swal from 'sweetalert2';

import { parseMoney } from '../utils/money';
import { FINANCIAL_EXPENSE_KEYS, FINANCIAL_INCOME_KEY, FINANCIAL_PARTNER_KEYS } from '../utils/financialSummary';

const Dashboard = () => {
    const { currentUser } = useAuth();
    const role = currentUser?.role?.toUpperCase() || 'OPERATIVO';
    const isAdminOrGerente = role === 'ADMIN' || role === 'GERENTE';
    const isSupervisor = role === 'SUPERVISOR';
    const isATC = role === 'ATENCIÓN AL CLIENTE'; // Todo ATC actúa como supervisor de su área
    // Filtrado ultra-flexible
    const isGC = currentUser?.role?.toUpperCase().includes('GESTIÓN') || currentUser?.role?.toUpperCase().includes('GESTION');
    const isMarketing = currentUser?.role?.toUpperCase().includes('MARKETING');
    const [isSupervisorATC, setIsSupervisorATC] = useState(false);
    // canSeeMuro: Admin, Gerente, Supervisor, ATC (todos), y GC
    const canSeeMuro = isAdminOrGerente || isSupervisor || isATC || isSupervisorATC;

    const [rutinasGerencia, setRutinasGerencia] = useState([]);
    const [rutinasActivas, setRutinasActivas] = useState([]);
    const [miTurnoHoy, setMiTurnoHoy] = useState(null);
    const [asistenciaActiva, setAsistenciaActiva] = useState(null);
    const [notas, setNotas] = useState([]);
    const [nuevaNota, setNuevaNota] = useState('');
    const [notaTipo, setNotaTipo] = useState('instruccion');
    const [pendientesVerificacion, setPendientesVerificacion] = useState(0);
    const [turnosHoyGlobal, setTurnosHoyGlobal] = useState([]);
    const [asistenciasHoyGlobal, setAsistenciasHoyGlobal] = useState([]);
    // Supervisor: notas_supervision y equipo departamental
    const [notasSupervision, setNotasSupervision] = useState([]);
    const [nuevaNotaSup, setNuevaNotaSup] = useState('');
    const [notaSupEstado, setNotaSupEstado] = useState('pendiente'); // 'pendiente' | 'resuelto'
    const [personalDep, setPersonalDep] = useState([]);       // empleados activos HOY en el dpto (ATC)
    const [personalSemanalDep, setPersonalSemanalDep] = useState([]); // todos los empleados del dpto (para grid semanal ATC)
    // Gestión de Cuentas
    const [personalGCDep, setPersonalGCDep] = useState([]);         // equipo GC activo hoy
    const [personalGCSemanal, setPersonalGCSemanal] = useState([]);  // equipo GC semanal
    const [notasGC, setNotasGC] = useState([]);
    const [nuevaNotaGC, setNuevaNotaGC] = useState('');
    const [notaGCEstado, setNotaGCEstado] = useState('pendiente');
    
    // Alta Gerencia State
    const [notasGerencia, setNotasGerencia] = useState([]);
    const [nuevaNotaGerencia, setNuevaNotaGerencia] = useState('');
    const [esAcuerdoJunta, setEsAcuerdoJunta] = useState(false);
    const [gastosGC, setGastosGC] = useState([]);    // histórico de gastos registrados por GC
    const [cantCuentasPausadas, setCantCuentasPausadas] = useState(0);
    const [stockBajo, setStockBajo] = useState([]); // servicios bajo punto de reorden
    const [misRutinasDiarias, setMisRutinasDiarias] = useState([]);
    const [rutinasDocId, setRutinasDocId] = useState(null);

    // Form gastos GC
    const [gcGastoPlataforma, setGcGastoPlataforma] = useState('');
    const [gcGastoMonto, setGcGastoMonto] = useState('');
    const [gcGastoMetodo, setGcGastoMetodo] = useState('Efectivo');
    const [gcGastoProveedor, setGcGastoProveedor] = useState('');
    const [gcGastoDesc, setGcGastoDesc] = useState('');
    const [configServicios, setConfigServicios] = useState([]);
    const [gastosPorRevisar, setGastosPorRevisar] = useState([]);
    // Marketing
    const [stockMarketing, setStockMarketing] = useState([]);
    const [notasMarketing, setNotasMarketing] = useState([]);
    const [nuevaNotaMarketing, setNuevaNotaMarketing] = useState('');
    const notaMarketingEstado = 'pendiente';
    const [marketingChecklist, setMarketingChecklist] = useState({
        envio_revendedores: false,
        produccion_tiktok: false,
        interaccion_grupos: false
    });
    const [marketingChecklistId, setMarketingChecklistId] = useState(null);
    const [rutinasProgreso, setRutinasProgreso] = useState({});
    const [rutinasProgresoId, setRutinasProgresoId] = useState(null);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            const umbral = configEmpresa.umbral_gasto_saludable !== undefined ? configEmpresa.umbral_gasto_saludable : 60;
            const ratio = data.PctGasto;
            
            let healthLabel = 'Óptima';
            let colorHex = '#10b981';
            if (ratio > umbral + 10) {
                healthLabel = 'Crítica';
                colorHex = '#ef4444';
            } else if (ratio > umbral) {
                healthLabel = 'En Observación';
                colorHex = '#f59e0b';
            }

            return (
                <div className="bg-slate-900 p-4 border border-slate-700 rounded-xl shadow-xl text-xs space-y-1 text-slate-200">
                    <p className="text-slate-400 font-bold mb-1">{label}</p>
                    {payload.map((entry, index) => (
                        <p key={index} className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color || entry.fill }} />
                            <span>{entry.name}: <span className="font-bold">${entry.value.toLocaleString()}</span></span>
                        </p>
                    ))}
                    <p style={{ color: colorHex }} className="font-bold border-t border-slate-700/50 pt-1 mt-1 flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorHex }} />
                        Salud Financiera: {healthLabel} ({ratio}%)
                    </p>
                </div>
            );
        }
        return null;
    };

    const [todasConfigRutinas, setTodasConfigRutinas] = useState([]);
    const [rutinasDiariasHoy, setRutinasDiariasHoy] = useState([]);
    const [historicoTareasPendientes, setHistoricoTareasPendientes] = useState([]);

    const [configEmpresa, setConfigEmpresa] = useState({ reserva_porcentaje: 0, umbral_gasto_saludable: 60 });
    const [isKpiModalOpen, setIsKpiModalOpen] = useState(false);
    const [filtroKpiMes, setFiltroKpiMes] = useState(new Date().getMonth() + 1);
    const [filtroKpiAnio, setFiltroKpiAnio] = useState(new Date().getFullYear());
    const [historicoKpis, setHistoricoKpis] = useState([]);
    const [kpisMesActual, setKpisMesActual] = useState(null);
    const [kpisMesAnterior, setKpisMesAnterior] = useState(null);

    const [formKPI, setFormKPI] = useState({
        mes: new Date().getMonth() + 1,
        anio: new Date().getFullYear(),
        suscripciones: '',
        clientes: '',
        revendedores: '',
        distribuidores: '',
        reportes: '',
        calificacion: '',
        suscripciones_perdidas: '',
        suscripciones_nuevas: ''
    });

    const calcularCumplimiento = (dep) => {
        const configDep = todasConfigRutinas.filter(r => r.departamento === dep);
        if (configDep.length === 0) return 0;

        let completadas = 0;
        let totalAsignadas = 0;

        rutinasDiariasHoy.forEach(docSnap => {
            const tareas = docSnap.rutinas || [];
            tareas.forEach(t => {
                const textoTarea = (t.texto || t.tarea || '').toLowerCase().trim();
                const pertenece = configDep.some(cd => (cd.tarea || '').toLowerCase().trim() === textoTarea);
                
                if (pertenece) {
                    totalAsignadas++;
                    if (t.completado) completadas++;
                }
            });
        });

        if (totalAsignadas === 0) return 0;
        return Math.round((completadas / totalAsignadas) * 100);
    };

    const getBarColor = (pct) => {
        if (pct < 40) return 'bg-red-500';
        if (pct < 70) return 'bg-yellow-500';
        return 'bg-emerald-500';
    };

    const getTextColor = (pct) => {
        if (pct < 40) return 'text-red-400';
        if (pct < 70) return 'text-yellow-400';
        return 'text-emerald-400';
    };

    const navigate = useNavigate();

    const roleToDep = {
        'GERENTE': 'Gerencia',
        'ATENCIÓN AL CLIENTE': 'Atención al Cliente',
        'GESTIÓN DE CUENTAS': 'Cuentas',
        'MARKETING': 'Marketing'
    };
    const userDep = roleToDep[role] || 'Gerencia';

    useEffect(() => {
        const q = query(collection(db, 'config_rutinas'), orderBy('orden', 'asc'));
        const unsub = onSnapshot(q, (snap) => {
            const docsData = snap.docs.map(d => ({id: d.id, ...d.data()}));
            setTodasConfigRutinas(docsData);
            setRutinasGerencia(docsData.filter(r => r.departamento === 'Gerencia'));
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        const unsub = onSnapshot(doc(db, 'config_empresa', 'main'), (snap) => {
            if (snap.exists()) {
                setConfigEmpresa(snap.data());
            }
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        const hoyStr = new Date().toISOString().split('T')[0];
        const q = query(collection(db, 'rutinas_diarias'), where('fecha', '==', hoyStr));
        const unsub = onSnapshot(q, (snap) => {
            setRutinasDiariasHoy(snap.docs.map(d => ({id: d.id, ...d.data()})));
        });
        return () => unsub();
    }, []);

    useEffect(() => {
        const fetchHistoricoPendientes = async () => {
            try {
                const hoyStr = new Date().toISOString().split('T')[0];
                const q = query(collection(db, 'rutinas_diarias'), where('fecha', '<', hoyStr));
                const snap = await getDocs(q);
                
                const pendientes = [];
                snap.docs.forEach(docSnap => {
                    const data = docSnap.data();
                    const tareasFaltantes = (data.rutinas || []).filter(r => !r.completado);
                    tareasFaltantes.forEach(t => {
                        pendientes.push({
                            fecha: data.fecha,
                            usuario: data.userName || 'Usuario',
                            tarea: t.texto || t.tarea
                        });
                    });
                });
                
                pendientes.sort((a, b) => b.fecha.localeCompare(a.fecha));
                setHistoricoTareasPendientes(pendientes);
            } catch (error) {
                console.error("Error al cargar histórico de tareas pendientes: ", error);
            }
        };
        fetchHistoricoPendientes();
    }, []);

    useEffect(() => {
        if (!isAdminOrGerente) return;
        const q = query(collection(db, 'kpis_mensuales'), where('mes', '==', Number(filtroKpiMes)), where('anio', '==', Number(filtroKpiAnio)));
        const unsub = onSnapshot(q, snap => {
            const fetched = snap.docs.map(d => ({id: d.id, ...d.data()}));
            const actual = fetched[0] || null;
            setKpisMesActual(actual);

            if (actual) {
                setFormKPI({
                    mes: actual.mes,
                    anio: actual.anio,
                    suscripciones: actual.suscripciones || '',
                    clientes: actual.clientes || '',
                    revendedores: actual.revendedores || '',
                    distribuidores: actual.distribuidores || '',
                    reportes: actual.reportes || '',
                    calificacion: actual.calificacion || ''
                });
            } else {
                setFormKPI({
                    mes: filtroKpiMes,
                    anio: filtroKpiAnio,
                    suscripciones: '',
                    clientes: '',
                    revendedores: '',
                    distribuidores: '',
                    reportes: '',
                    calificacion: ''
                });
            }
        });
        return () => unsub();
    }, [isAdminOrGerente, filtroKpiMes, filtroKpiAnio]);

    useEffect(() => {
        if (!isAdminOrGerente) return;
        const q = query(collection(db, 'kpis_mensuales'));
        const unsub = onSnapshot(q, snap => {
            const fetched = snap.docs.map(d => ({id: d.id, ...d.data()}))
                .sort((a, b) => {
                    if (Number(a.anio) !== Number(b.anio)) return Number(a.anio) - Number(b.anio);
                    return Number(a.mes) - Number(b.mes);
                });
            setHistoricoKpis(fetched);

            const antM = Number(filtroKpiMes) === 1 ? 12 : Number(filtroKpiMes) - 1;
            const antY = Number(filtroKpiMes) === 1 ? Number(filtroKpiAnio) - 1 : Number(filtroKpiAnio);
            const anterior = fetched.find(k => Number(k.mes) === antM && Number(k.anio) === antY);
            setKpisMesAnterior(anterior);
        });
        return () => unsub();
    }, [isAdminOrGerente, filtroKpiMes, filtroKpiAnio]);





    const last6MonthsData = useMemo(() => {
        if (historicoKpis.length === 0) return [];
        return historicoKpis.slice(-6).map(k => ({
            name: `${k.mes}/${k.anio.toString().substring(2)}`,
            Suscripciones: Number(k.suscripciones) || 0,
            Clientes: Number(k.clientes) || 0
        }));
    }, [historicoKpis]);

    const isDiaUno = new Date().getDate() === 1;
    const showKpiBanner = isDiaUno && isAdminOrGerente && !kpisMesActual;

    const handleSaveKPIs = async (e) => {
        e.preventDefault();
        try {
            const curM = Number(formKPI.mes);
            const curY = Number(formKPI.anio);
            const docId = `kpi_${curY}_${curM}`;
            
            await setDoc(doc(db, 'kpis_mensuales', docId), {
                mes: curM,
                anio: curY,
                suscripciones: Number(formKPI.suscripciones) || 0,
                clientes: Number(formKPI.clientes) || 0,
                revendedores: Number(formKPI.revendedores) || 0,
                distribuidores: Number(formKPI.distribuidores) || 0,
                reportes: Number(formKPI.reportes) || 0,
                calificacion: Number(formKPI.calificacion) || 0,
                suscripciones_perdidas: Number(formKPI.suscripciones_perdidas) || 0,
                suscripciones_nuevas: Number(formKPI.suscripciones_nuevas) || 0,
                updatedAt: serverTimestamp()
            });

            Swal.fire({ title: 'KPIs Guardados', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            setIsKpiModalOpen(false);
        } catch (err) {
            Swal.fire('Error', 'No se pudieron registrar los KPIs', 'error');
        }
    };

    const renderKpiBanner = () => {
        if (!showKpiBanner) return null;
        return (
            <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-xl flex items-center justify-between gap-4 shadow-md animate-pulse mb-6">
                <div className="flex items-center gap-3">
                    <AlertTriangle className="text-amber-500 flex-shrink-0" size={24} />
                    <span className="text-amber-400 text-sm font-semibold">⚠️ Acción Requerida: Es momento de cargar los KPIs Operacionales del mes.</span>
                </div>
                <button 
                    onClick={() => setIsKpiModalOpen(true)}
                    className="bg-amber-600 hover:bg-amber-500 text-white font-bold px-4 py-1.5 rounded-lg text-xs transition-colors shadow"
                >
                    Cargar Ahora
                </button>
            </div>
        );
    };

    const renderPanelKPIs = () => {
        if (!isAdminOrGerente) return null;

        const compare = (valAct, valAnt, inverse = false) => {
            if (valAnt === undefined || valAnt === null) return { diff: '--', color: 'text-slate-400' };
            const act = Number(valAct) || 0;
            const ant = Number(valAnt) || 0;
            if (ant === 0) return { diff: 'N/A', color: 'text-emerald-400' };

            const diff = act - ant;
            const pct = ((diff / ant) * 100).toFixed(1);
            if (diff > 0) return { diff: `+${diff.toLocaleString()} (+${pct}%)`, color: inverse ? 'text-red-400' : 'text-emerald-400' };
            if (diff < 0) return { diff: `${diff.toLocaleString()} (${pct}%)`, color: inverse ? 'text-emerald-400' : 'text-red-400' };
            return { diff: '0 (0%)', color: 'text-slate-400' };
        };

        const calculateRates = (kpis) => {
            if (!kpis) return { churn: 0, retencion: 0 };
            const perdidas = Number(kpis.suscripciones_perdidas) || 0;
            const totalSuscripciones = Number(kpis.suscripciones) || 0;
            if (totalSuscripciones === 0) return { churn: 0, retencion: 100 };
            const churn = (perdidas / totalSuscripciones) * 100;
            return { churn: Number(churn.toFixed(2)), retencion: Number((100 - churn).toFixed(2)) };
        };

        const ratesActual = calculateRates(kpisMesActual);
        const ratesAnterior = calculateRates(kpisMesAnterior);

        const metrics = [
            { id: 'suscripciones', label: 'Suscripciones Activas', act: kpisMesActual?.suscripciones, ant: kpisMesAnterior?.suscripciones },
            { id: 'clientes', label: 'Clientes Activos', act: kpisMesActual?.clientes, ant: kpisMesAnterior?.clientes },
            { id: 'revendedores', label: 'Revendedores Activos', act: kpisMesActual?.revendedores, ant: kpisMesAnterior?.revendedores },
            { id: 'distribuidores', label: 'Distribuidores Activos', act: kpisMesActual?.distribuidores, ant: kpisMesAnterior?.distribuidores },
            { id: 'reportes', label: 'Reportes de Soporte', act: kpisMesActual?.reportes, ant: kpisMesAnterior?.reportes },
            { id: 'calificacion', label: 'Calificación Promedio', act: kpisMesActual?.calificacion, ant: kpisMesAnterior?.calificacion },
            { id: 'suscripciones_perdidas', label: 'Suscripciones Perdidas', act: kpisMesActual?.suscripciones_perdidas || 0, ant: kpisMesAnterior?.suscripciones_perdidas || 0 },
            { id: 'suscripciones_nuevas', label: 'Suscripciones Nuevas', act: kpisMesActual?.suscripciones_nuevas || 0, ant: kpisMesAnterior?.suscripciones_nuevas || 0 },
            { id: 'churn', label: 'Churn Rate (%)', act: ratesActual.churn, ant: ratesAnterior.churn, inverse: true },
            { id: 'retencion', label: 'Tasa de Retención (%)', act: ratesActual.retencion, ant: ratesAnterior.retencion }
        ];

        return (
            <div className="bg-slate-800/80 border border-slate-700/60 p-6 rounded-2xl shadow-xl mt-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                        <h3 className="text-white font-bold text-lg flex items-center gap-2">
                            <TrendingUp className="text-cyan-400" size={20} /> KPIs Operacionales
                        </h3>
                        <p className="text-slate-400 text-xs mt-1">Navegación histórica y comparativa mensual</p>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
                        <div className="flex gap-2">
                            <select 
                                value={filtroKpiMes} 
                                onChange={(e) => setFiltroKpiMes(Number(e.target.value))} 
                                className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-cyan-500 font-bold"
                            >
                                {['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'].map((m, idx) => (
                                    <option key={idx+1} value={idx+1}>{m}</option>
                                ))}
                            </select>
                            <select 
                                value={filtroKpiAnio} 
                                onChange={(e) => setFiltroKpiAnio(Number(e.target.value))} 
                                className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-cyan-500 font-bold"
                            >
                                {Array.from({length: 5}, (_, i) => new Date().getFullYear() - 2 + i).map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>

                        <button 
                            onClick={() => setIsKpiModalOpen(true)}
                            className="bg-cyan-600 hover:bg-cyan-500 text-white font-bold px-4 py-1.5 rounded-lg text-xs transition-colors shadow-md w-full sm:w-auto text-center"
                        >
                            {kpisMesActual ? 'Editar KPIs' : 'Cargar KPIs'}
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-300">
                        <thead className="text-xs text-slate-400 uppercase bg-slate-900/60 border-b border-slate-700">
                            <tr>
                                <th className="px-4 py-3">Métrica</th>
                                <th className="px-4 py-3 text-center">Mes Anterior</th>
                                <th className="px-4 py-3 text-center">Mes Seleccionado</th>
                                <th className="px-4 py-3 text-right">Tendencia</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {metrics.map(m => {
                                const comp = compare(m.act, m.ant, m.inverse);
                                return (
                                    <tr key={m.id} className="hover:bg-slate-700/20 transition-colors">
                                        <td className="px-4 py-3 font-semibold text-slate-200">{m.label}</td>
                                        <td className="px-4 py-3 text-center text-slate-400 font-mono">{m.ant !== undefined && m.ant !== null ? Number(m.ant).toLocaleString() : '--'}</td>
                                        <td className="px-4 py-3 text-center text-white font-bold font-mono">{m.act !== undefined && m.act !== null ? Number(m.act).toLocaleString() : 'Pendiente'}</td>
                                        <td className={`px-4 py-3 text-right font-mono font-bold text-xs ${comp.color}`}>{comp.diff}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {last6MonthsData.length > 0 && (
                    <div className="mt-6 border-t border-slate-700/40 pt-4">
                        <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center gap-1">
                            <TrendingUp size={14} className="text-cyan-400" /> Tendencia Operativa (Últimos 6 meses)
                        </h4>
                        <div className="h-[140px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={last6MonthsData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.2} />
                                    <XAxis dataKey="name" stroke="#64748b" fontSize={10} />
                                    <YAxis stroke="#64748b" fontSize={10} />
                                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '6px', color: '#f8fafc', fontSize: '11px' }} />
                                    <Legend iconSize={8} wrapperStyle={{ fontSize: '10px' }} />
                                    <Line type="monotone" dataKey="Suscripciones" stroke="#22d3ee" strokeWidth={2} dot={{ r: 3 }} />
                                    <Line type="monotone" dataKey="Clientes" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderKPIModal = () => {
        if (!isKpiModalOpen) return null;
        return (
            <div className="fixed inset-0 bg-black/80 flex justify-center items-center z-50 p-4 animate-in fade-in duration-200">
                <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700 shadow-2xl">
                    <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                        <TrendingUp className="text-cyan-400" size={20} /> Cargar KPIs Operacionales
                    </h2>
                    
                    <form onSubmit={handleSaveKPIs} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Mes</label>
                                <select 
                                    value={formKPI.mes} 
                                    onChange={e => setFormKPI({...formKPI, mes: Number(e.target.value)})}
                                    className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm"
                                >
                                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                                        <option key={m} value={m}>Mes {m}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Año</label>
                                <input 
                                    type="number" 
                                    value={formKPI.anio} 
                                    onChange={e => setFormKPI({...formKPI, anio: Number(e.target.value)})}
                                    className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Suscripciones Activas</label>
                            <input type="number" value={formKPI.suscripciones} onChange={e => setFormKPI({...formKPI, suscripciones: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Clientes Activos</label>
                            <input type="number" value={formKPI.clientes} onChange={e => setFormKPI({...formKPI, clientes: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Revendedores Activos</label>
                            <input type="number" value={formKPI.revendedores} onChange={e => setFormKPI({...formKPI, revendedores: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Distribuidores Activos</label>
                            <input type="number" value={formKPI.distribuidores} onChange={e => setFormKPI({...formKPI, distribuidores: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Reportes de Soporte</label>
                            <input type="number" value={formKPI.reportes} onChange={e => setFormKPI({...formKPI, reportes: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                        </div>

                        <div>
                            <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Calificación Promedio (1-5)</label>
                            <input type="number" step="0.1" max="5" min="1" value={formKPI.calificacion} onChange={e => setFormKPI({...formKPI, calificacion: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Suscrip. Perdidas</label>
                                <input type="number" value={formKPI.suscripciones_perdidas} onChange={e => setFormKPI({...formKPI, suscripciones_perdidas: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-slate-400 uppercase mb-1">Suscrip. Nuevas</label>
                                <input type="number" value={formKPI.suscripciones_nuevas} onChange={e => setFormKPI({...formKPI, suscripciones_nuevas: e.target.value})} className="w-full bg-slate-700 text-white p-2 rounded-md border border-slate-600 focus:outline-none focus:border-cyan-500 text-sm font-mono" required />
                            </div>
                        </div>

                        <div className="flex justify-end space-x-4 pt-4">
                            <button type="button" onClick={() => setIsKpiModalOpen(false)} className="py-2 px-4 rounded-md bg-slate-600 hover:bg-slate-500 transition-colors text-white text-xs font-semibold">Cancelar</button>
                            <button type="submit" className="py-2 px-4 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold transition-colors">Guardar Métricas</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };


    // Load isSupervisorATC flag from users collection
    useEffect(() => {
        if (!currentUser?.uid) return;
        const unsub = onSnapshot(doc(db, 'users', currentUser.uid), snap => {
            if (snap.exists()) setIsSupervisorATC(!!snap.data().isSupervisor);
        });
        return () => unsub();
    }, [currentUser]);

    useEffect(() => {
        if (!isAdminOrGerente) {
            const qActivas = query(collection(db, 'config_rutinas'), orderBy('orden', 'asc'));
            const unsubActivas = onSnapshot(qActivas, (snap) => setRutinasActivas(snap.docs.map(d => ({id: d.id, ...d.data()})).filter(r => r.departamento === userDep)));
            return () => unsubActivas();
        }
    }, [isAdminOrGerente, userDep]);


    useEffect(() => {
        if (!currentUser?.email) return;
        const todayStr = new Date().toISOString().slice(0, 10);
        const q = query(
            collection(db, 'rutinas_progreso_diario'),
            where('email', '==', currentUser.email),
            where('fecha_texto', '==', todayStr)
        );
        const unsub = onSnapshot(q, snap => {
            if (!snap.empty) {
                const docData = snap.docs[0];
                setRutinasProgresoId(docData.id);
                setRutinasProgreso(docData.data().checks || {});
            } else {
                setRutinasProgresoId(null);
                setRutinasProgreso({});
            }
        });
        return () => unsub();
    }, [currentUser]);

    useEffect(() => {
        if (isAdminOrGerente || !currentUser?.email) return;
        
        const todayStr = new Date().toISOString().slice(0, 10);
        const todayDayIndex = new Date().getDay();

        const qTurnos = query(collection(db, 'personal'), where('email', '==', currentUser.email));
        const unsubT = onSnapshot(qTurnos, snap => {
            if (!snap.empty) {
                const staff = snap.docs[0].data();
                const hm = staff.horario_maestro?.[todayDayIndex];
                if (hm && hm.activo) {
                    setMiTurnoHoy({ id: snap.docs[0].id, ...hm });
                } else {
                    setMiTurnoHoy(null);
                }
            } else {
                setMiTurnoHoy(null);
            }
        });

        const qAsis = query(
            collection(db, 'asistencia_logs'),
            where('email', '==', currentUser.email),
            where('fecha_texto', '==', todayStr)
        );
        const unsubA = onSnapshot(qAsis, snap => {
            if (!snap.empty) {
                setAsistenciaActiva({ id: snap.docs[0].id, ...snap.docs[0].data() });
            } else {
                setAsistenciaActiva(null);
            }
        });

        return () => { unsubT(); unsubA(); };
    }, [currentUser, isAdminOrGerente]);

    useEffect(() => {
        if (!canSeeMuro) return;
        const q = query(collection(db, 'dashboard_notas'));
        const unsub = onSnapshot(q, snap => {
            const fetched = snap.docs.map(d => ({id: d.id, ...d.data()}));
            fetched.sort((a,b) => b.fecha - a.fecha);
            setNotas(fetched);
        });
        return () => unsub();
    }, [canSeeMuro]);

    useEffect(() => {
        if (!isAdminOrGerente) return;
        const q = query(collection(db, 'notas_gerencia'), orderBy('fecha', 'desc'));
        const unsub = onSnapshot(q, snap => {
            const fetched = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setNotasGerencia(fetched);
        });
        return () => unsub();
    }, [isAdminOrGerente]);

    useEffect(() => {
        if (!canSeeMuro) return;
        const q = query(collection(db, 'inventario_bitacora'), where('verificado', '==', false));
        const unsub = onSnapshot(q, snap => {
            setPendientesVerificacion(snap.docs.length);
        });
        return () => unsub();
    }, [canSeeMuro]);

    useEffect(() => {
        if (!canSeeMuro) return;
        const todayStr = new Date().toISOString().slice(0, 10);
        const todayDayIndex = new Date().getDay();

        const qTurnos = query(collection(db, 'personal'));
        const unsubT = onSnapshot(qTurnos, snap => {
            const activeStaffToday = [];
            snap.docs.forEach(d => {
                const data = d.data();
                const hm = data.horario_maestro?.[todayDayIndex];
                if (hm && hm.activo) {
                    activeStaffToday.push({
                        id: d.id,
                        email: data.email,
                        nombre: data.nombre,
                        horario: `${hm.hora_inicio} - ${hm.hora_fin}`,
                        lunch: `${hm.inicio_almuerzo} - ${hm.fin_almuerzo}`
                    });
                }
            });
            setTurnosHoyGlobal(activeStaffToday);
        });

        const qAsis = query(collection(db, 'asistencia_logs'), where('fecha_texto', '==', todayStr));
        const unsubA = onSnapshot(qAsis, snap => {
            setAsistenciasHoyGlobal(snap.docs.map(d => ({id: d.id, ...d.data()})));
        });

        return () => { unsubT(); unsubA(); };
    }, [canSeeMuro]);

    // Notas de Supervisión (notas_supervision)
    useEffect(() => {
        if (!canSeeMuro) return;
        const q = query(collection(db, 'notas_supervision'));
        const unsub = onSnapshot(q, snap => {
            const fetched = snap.docs.map(d => ({id: d.id, ...d.data()}));
            fetched.sort((a, b) => (b.fecha || 0) - (a.fecha || 0));
            setNotasSupervision(fetched);
        });
        return () => unsub();
    }, [canSeeMuro]);

    // Personal del departamento del supervisor
    useEffect(() => {
        if (!canSeeMuro) return;
        const todayDayIndex = new Date().getDay();

        const unsub = onSnapshot(query(collection(db, 'personal')), snap => {
            const misDep = [];
            snap.docs.forEach(d => {
                const data = d.data();
                const dep = data.departamento || data.rol;
                // Supervisor ve solo su departamento. Admin/Gerente ven todos.
                const depActual = (() => {
                    if (isSupervisor || isATC || isSupervisorATC) return 'Atención al Cliente';
                    return null; // Admin ve todos
                })();
                if (!isAdminOrGerente && depActual && dep !== depActual) return;

                const hm = data.horario_maestro?.[todayDayIndex];
                if (hm && hm.activo) {
                    misDep.push({
                        id: d.id,
                        email: data.email,
                        nombre: data.nombre,
                        departamento: dep,
                        horario: `${hm.hora_inicio} → ${hm.hora_fin}`,
                        lunch: `${hm.inicio_almuerzo} - ${hm.fin_almuerzo}`,
                        descansos: hm.descansos || []
                    });
                }
            });
            setPersonalDep(misDep);
        });
        return () => unsub();
    }, [canSeeMuro, isSupervisor, isATC, isSupervisorATC, isAdminOrGerente]);

    useEffect(() => {
        const fetchRutinas = async () => {
            if (!currentUser?.uid) return;
            if (!isATC && !isGC) return;

            const hoyStr = new Date().toISOString().split('T')[0];
            const q = query(
                collection(db, 'rutinas_diarias'),
                where('userId', '==', currentUser.uid),
                where('fecha', '==', hoyStr)
            );

            const snap = await getDocs(q);
            if (!snap.empty) {
                const docSnap = snap.docs[0];
                setRutinasDocId(docSnap.id);
                setMisRutinasDiarias(docSnap.data().rutinas || []);
            } else {
                const baseList = isATC ? [
                    "10:00 AM — Cambiar la Tasa del dólar.",
                    "Resolver solicitudes pendientes del día anterior.",
                    "01:00 PM — Cambiar la Tasa del dólar.",
                    "03:00 PM — Revisión de chats para asuntos pendientes.",
                    "Enviar los mensajes de cobro.",
                    "Revisión de reportes.",
                    "09:00 PM — Revisión de chats para asuntos pendientes.",
                    "Cambio de estado en Chat Hoprin+ a 'Ausente'."
                ] : [
                    "Enviar Informe de Stock al Gerente.",
                    "Enviar Informe de Cuentas por Vencer al Gerente.",
                    "Resolver Reportes.",
                    "Realizar cortes de cuentas (más de 2 días sin pagar).",
                    "03:00 PM — Revisión de asuntos pendientes.",
                    "05:00 PM — Actualización del Informe de Stock.",
                    "Renovar Cuentas.",
                    "Realizar pruebas aleatorias de estabilidad.",
                    "09:00 PM — Revisión de asuntos pendientes.",
                    "Cambio de estado en Chat Hoprin+ a 'Ausente'."
                ];
                
                const initialRutinas = baseList.map((item, idx) => ({
                    id: idx,
                    texto: item,
                    completado: false
                }));

                try {
                    const newDoc = await addDoc(collection(db, 'rutinas_diarias'), {
                        userId: currentUser.uid,
                        userName: currentUser.name || currentUser.email,
                        fecha: hoyStr,
                        rutinas: initialRutinas,
                        created_at: serverTimestamp()
                    });
                    setRutinasDocId(newDoc.id);
                    setMisRutinasDiarias(initialRutinas);
                } catch(e) {
                    console.error("Error inicializando rutinas", e);
                }
            }
        };

        fetchRutinas();

        if (currentUser?.uid && (isATC || isGC)) {
            const hoyStr = new Date().toISOString().split('T')[0];
            const q = query(
                collection(db, 'rutinas_diarias'),
                where('userId', '==', currentUser.uid),
                where('fecha', '==', hoyStr)
            );
            const unsub = onSnapshot(q, (snapshot) => {
                if (!snapshot.empty) {
                    const d = snapshot.docs[0];
                    setRutinasDocId(d.id);
                    setMisRutinasDiarias(d.data().rutinas || []);
                }
            });
            return () => unsub();
        }
    }, [currentUser, isATC, isGC]);

    const handleToggleRutina = async (rutinaId) => {
        if (!rutinasDocId) return;
        const nuevasRutinas = misRutinasDiarias.map(r => 
            r.id === rutinaId ? { ...r, completado: !r.completado } : r
        );
        setMisRutinasDiarias(nuevasRutinas);
        try {
            await updateDoc(doc(db, 'rutinas_diarias', rutinasDocId), {
                rutinas: nuevasRutinas
            });
        } catch(e) {
            console.error("Error actualizando rutina", e);
        }
    };

    // Personal semanal del departamento (para la grilla Horario Maestro)
    useEffect(() => {
        if (!canSeeMuro) return;
        const unsub = onSnapshot(query(collection(db, 'personal')), snap => {
            const lista = [];
            snap.docs.forEach(d => {
                const data = d.data();
                const dep = data.departamento || data.rol;
                const depFiltro = (isSupervisor || isATC || isSupervisorATC) ? 'Atención al Cliente' : null;
                if (!isAdminOrGerente && depFiltro && dep !== depFiltro) return;
                lista.push({
                    id: d.id,
                    nombre: data.nombre,
                    departamento: dep,
                    horario_maestro: data.horario_maestro || []
                });
            });
            setPersonalSemanalDep(lista);
        });
        return () => unsub();
    }, [canSeeMuro, isSupervisor, isATC, isSupervisorATC, isAdminOrGerente]);

    // ── Gestión de Cuentas: equipo hoy ───────────────────────────────────
    useEffect(() => {
        if (!isGC && !isAdminOrGerente) return;
        const todayDayIndex = new Date().getDay();
        const unsub = onSnapshot(query(collection(db, 'personal')), snap => {
            const lista = [];
            snap.docs.forEach(d => {
                const data = d.data();
                const dep = data.departamento || data.rol;
                if (dep !== 'Gestión de Cuentas') return;
                const hm = data.horario_maestro?.[todayDayIndex];
                if (hm && hm.activo) {
                    lista.push({
                        id: d.id, email: data.email, nombre: data.nombre,
                        horario: `${hm.hora_inicio} → ${hm.hora_fin}`,
                        lunch: `${hm.inicio_almuerzo} - ${hm.fin_almuerzo}`,
                        descansos: hm.descansos || []
                    });
                }
            });
            setPersonalGCDep(lista);
        });
        return () => unsub();
    }, [isGC, isAdminOrGerente]);

    // ── Gestión de Cuentas: equipo semanal ───────────────────────────────
    useEffect(() => {
        if (!isGC && !isAdminOrGerente) return;
        const unsub = onSnapshot(query(collection(db, 'personal')), snap => {
            const lista = [];
            snap.docs.forEach(d => {
                const data = d.data();
                const dep = data.departamento || data.rol;
                if (dep !== 'Gestión de Cuentas') return;
                lista.push({ id: d.id, nombre: data.nombre, horario_maestro: data.horario_maestro || [] });
            });
            setPersonalGCSemanal(lista);
        });
        return () => unsub();
    }, [isGC, isAdminOrGerente]);

    // ── GC Notas ──────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isGC && !isAdminOrGerente) return;
        const unsub = onSnapshot(query(collection(db, 'notas_gc'), orderBy('fecha', 'desc')), snap => {
            setNotasGC(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [isGC, isAdminOrGerente]);

    useEffect(() => {
        if (!isGC && !isAdminOrGerente) return;
        const unsub = onSnapshot(collection(db, 'inventario_cuentas'), snap => {
            const count = snap.docs.filter(d => d.data().estado === 'Pausada / Reporte').length;
            setCantCuentasPausadas(count);
        });
        return () => unsub();
    }, [isGC, isAdminOrGerente]);

    // ── GC Gastos (filtrados por origen) ──────────────────────────────────
    useEffect(() => {
        if (!isGC && !isAdminOrGerente) return;
        const unsub = onSnapshot(
            query(collection(db, 'finanzas_movimientos'), orderBy('created_at', 'desc')),
            snap => {
                const gcMov = snap.docs
                    .map(d => ({ id: d.id, ...d.data() }))
                    .filter(d => d.origen === 'gestion_cuentas');
                setGastosGC(gcMov);
            }
        );
        return () => unsub();
    }, [isGC, isAdminOrGerente]);

    // ── Stock bajo punto de reorden ───────────────────────────────────────
    useEffect(() => {
        if (!isGC && !isAdminOrGerente) return;
        const unsub = onSnapshot(query(collection(db, 'almacen_stock')), snap => {
            const bajo = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(d => (d.unidades ?? 0) < (d.punto_de_reorden ?? 5));
            setStockBajo(bajo);
        });
        return () => unsub();
    }, [isGC, isAdminOrGerente]);

    // ── Config Servicios (selector de plataforma en form gastos) ─────────
    useEffect(() => {
        if (!isGC && !isAdminOrGerente) return;
        const unsub = onSnapshot(query(collection(db, 'config_servicios')), snap => {
            setConfigServicios(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [isGC, isAdminOrGerente]);

    // ── GC: guardar gasto operativo ──
    // Escribe en finanzas_movimientos (contabilidad) y finanzas_gastos (colección visible en Firebase GC)
    const handleGCGasto = async (e) => {
        e.preventDefault();
        const montoNormalizado = parseMoney(gcGastoMonto);
        if (montoNormalizado === null || !gcGastoPlataforma) return;
        const payload = {
            tipo: 'gasto',
            categoria: 'Inversión Inventario',
            descripcion: gcGastoDesc || `Compra/Renovación - ${gcGastoPlataforma}`,
            plataforma: gcGastoPlataforma,
            proveedor: gcGastoProveedor,
            metodo_pago: gcGastoMetodo,
            monto: montoNormalizado,
            amount: montoNormalizado,
            origen: 'gestion_cuentas',
            registrado_por: currentUser?.name || currentUser?.email,
            created_at: serverTimestamp(),
            fecha: new Date().getTime()
        };
        try {
            await Promise.all([
                addDoc(collection(db, 'finanzas_movimientos'), payload),
                addDoc(collection(db, 'finanzas_gastos'), payload)
            ]);
            setGcGastoPlataforma('');
            setGcGastoMonto('');
            setGcGastoProveedor('');
            setGcGastoDesc('');
            setGcGastoMetodo('Efectivo');
            Swal.fire({ title: '¡Gasto Registrado!', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1800 });
        } catch (err) {
            console.error('GC Gasto error:', err);
            Swal.fire('Error', 'No se pudo guardar el gasto: ' + err.message, 'error');
        }
    };

    // ── Mkt: Top 3 Stock para Empuje Comercial ──
    useEffect(() => {
        if (!isMarketing && !isAdminOrGerente) return;
        const unsub = onSnapshot(query(collection(db, 'almacen_stock')), snap => {
            const stock = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            stock.sort((a, b) => (b.unidades || 0) - (a.unidades || 0));
            setStockMarketing(stock.slice(0, 3));
        });
        return () => unsub();
    }, [isMarketing, isAdminOrGerente]);

    // ── Mkt: Agenda Diaria (Progreso de Firebase) ──
    useEffect(() => {
        if (!isMarketing && !currentUser?.email) return;
        const todayStr = new Date().toISOString().slice(0, 10);
        const q = query(
            collection(db, 'marketing_progreso_diario'),
            where('email', '==', currentUser.email),
            where('fecha_texto', '==', todayStr)
        );
        const unsub = onSnapshot(q, snap => {
            if (!snap.empty) {
                const docData = snap.docs[0];
                setMarketingChecklistId(docData.id);
                setMarketingChecklist(docData.data().checks || {
                    envio_revendedores: false, produccion_tiktok: false, interaccion_grupos: false
                });
            } else {
                setMarketingChecklistId(null);
                setMarketingChecklist({ envio_revendedores: false, produccion_tiktok: false, interaccion_grupos: false });
            }
        });
        return () => unsub();
    }, [isMarketing, currentUser]);

    // ── Mkt: Toggle Check ──
    const handleToggleMarketingCheck = async (key) => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const newChecks = { ...marketingChecklist, [key]: !marketingChecklist[key] };
        
        try {
            if (marketingChecklistId) {
                await updateDoc(doc(db, 'marketing_progreso_diario', marketingChecklistId), {
                    checks: newChecks,
                    updated_at: serverTimestamp()
                });
            } else {
                await addDoc(collection(db, 'marketing_progreso_diario'), {
                    email: currentUser.email,
                    nombre: currentUser.name || currentUser.email,
                    fecha_texto: todayStr,
                    checks: newChecks,
                    created_at: serverTimestamp(),
                    updated_at: serverTimestamp()
                });
            }
        } catch (err) {
            console.error('Error al actualizar checklist Mkt:', err);
            Swal.fire('Error', 'No se pudo guardar el progreso', 'error');
        }
    };

    const handleToggleRoutineCheck = async (routineId) => {
        const todayStr = new Date().toISOString().slice(0, 10);
        const newChecks = { ...rutinasProgreso, [routineId]: !rutinasProgreso[routineId] };
        
        try {
            if (rutinasProgresoId) {
                await updateDoc(doc(db, 'rutinas_progreso_diario', rutinasProgresoId), {
                    checks: newChecks,
                    updated_at: serverTimestamp()
                });
            } else {
                await addDoc(collection(db, 'rutinas_progreso_diario'), {
                    email: currentUser.email,
                    nombre: currentUser.name || currentUser.email,
                    fecha_texto: todayStr,
                    checks: newChecks,
                    created_at: serverTimestamp(),
                    updated_at: serverTimestamp()
                });
            }
        } catch (err) {
            console.error('Error al actualizar checklist de rutina:', err);
            Swal.fire('Error', 'No se pudo guardar el progreso', 'error');
        }
    };

    // ── Mkt: Notas de Estrategia ──
    useEffect(() => {
        if (!isMarketing && !isAdminOrGerente) return;
        const unsub = onSnapshot(query(collection(db, 'notas_marketing'), orderBy('fecha', 'desc')), snap => {
            setNotasMarketing(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        return () => unsub();
    }, [isMarketing, isAdminOrGerente]);

    const handleMarketingNota = async (e) => {
        e.preventDefault();
        if (!nuevaNotaMarketing.trim()) return;
        try {
            await addDoc(collection(db, 'notas_marketing'), {
                mensaje: nuevaNotaMarketing,
                estado: notaMarketingEstado,
                autor: currentUser?.name || currentUser?.email,
                fecha: new Date().getTime(),
                created_at: serverTimestamp()
            });
            setNuevaNotaMarketing('');
        } catch (err) {
            console.error(err);
        }
    };

    const handleSaveNotaGerencia = async (e) => {
        e.preventDefault();
        if (!nuevaNotaGerencia.trim()) return;
        try {
            await addDoc(collection(db, 'notas_gerencia'), {
                mensaje: nuevaNotaGerencia,
                autor: currentUser?.name || currentUser?.email,
                fecha: new Date().getTime(),
                esJunta: esAcuerdoJunta,
                created_at: serverTimestamp()
            });
            setNuevaNotaGerencia('');
            setEsAcuerdoJunta(false);
        } catch (err) {
            console.error(err);
            Swal.fire('Error', 'No se pudo guardar la nota estratégica', 'error');
        }
    };

    const handleEliminarNotaGerencia = async (id) => {
        const result = await Swal.fire({
            title: '¿Eliminar nota?',
            text: '¿Estás seguro de limpiar este registro estratégico?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(db, 'notas_gerencia', id));
                Swal.fire({ title: 'Nota Eliminada', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } catch (err) {
                console.error(err);
                Swal.fire('Error', 'No se pudo eliminar la nota', 'error');
            }
        }
    };

    const handleEditarNotaGerencia = async (nota) => {
        const { value: nuevoMensaje } = await Swal.fire({
            title: 'Editar Nota Estratégica',
            input: 'textarea',
            inputValue: nota.mensaje,
            inputLabel: 'Mensaje',
            showCancelButton: true,
            inputValidator: (value) => {
                if (!value) return '¡Debes escribir un mensaje!';
            }
        });
        if (nuevoMensaje) {
            try {
                await updateDoc(doc(db, 'notas_gerencia', nota.id), {
                    mensaje: nuevoMensaje,
                    editado: true,
                    updated_at: serverTimestamp()
                });
                Swal.fire({ title: 'Nota Actualizada', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
            } catch (err) {
                console.error(err);
                Swal.fire('Error', 'No se pudo actualizar la nota', 'error');
            }
        }
    };

    const handleToggleNotaMarketing = async (id, estadoActual) => {
        try {
            await updateDoc(doc(db, 'notas_marketing', id), {
                estado: estadoActual === 'pendiente' ? 'resuelto' : 'pendiente'
            });
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        if (!isAdminOrGerente) return;
        const unsub = onSnapshot(query(collection(db, 'finanzas_gastos')), snap => {
            const unreviewed = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(d => d.revisado !== true);
            setGastosPorRevisar(unreviewed);
        });
        return () => unsub();
    }, [isAdminOrGerente]);

    const handleConfirmarGasto = async (gastoId) => {
        try {
            await updateDoc(doc(db, 'finanzas_gastos', gastoId), { revisado: true });
            Swal.fire({ title: '¡Gasto Confirmado!', icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 1500 });
        } catch (err) {
            console.error('Error al confirmar gasto:', err);
            Swal.fire('Error', 'No se pudo confirmar el gasto', 'error');
        }
    };

    // ── GC: publicar nota ─────────────────────────────────────────────────
    const handleGCNota = async (e) => {
        e.preventDefault();
        if (!nuevaNotaGC.trim()) return;
        try {
            await addDoc(collection(db, 'notas_gc'), {
                mensaje: nuevaNotaGC,
                estado: notaGCEstado,
                autor: currentUser?.name || currentUser?.email,
                fecha: new Date().getTime(),
                createdAt: serverTimestamp()
            });
            setNuevaNotaGC('');
        } catch (err) { console.error(err); }
    };

    // ── GC: toggle estado nota ────────────────────────────────────────────
    const handleToggleNotaGC = async (id, estadoActual) => {
        try {
            await updateDoc(doc(db, 'notas_gc', id), {
                estado: estadoActual === 'pendiente' ? 'resuelto' : 'pendiente'
            });
        } catch (err) { console.error(err); }
    };

    const handleAddNota = async (e) => {
        e.preventDefault();
        if (!nuevaNota.trim()) return;
        try {
            await addDoc(collection(db, 'dashboard_notas'), {
                mensaje: nuevaNota,
                tipo: notaTipo,
                autor: currentUser.name || currentUser.email,
                fecha: new Date().getTime(),
                createdAt: serverTimestamp()
            });
            setNuevaNota('');
        } catch(error) {
            console.error('Error adding nota', error);
        }
    };

    const handleAddNotaSupervision = async (e) => {
        e.preventDefault();
        if (!nuevaNotaSup.trim()) return;
        try {
            await addDoc(collection(db, 'notas_supervision'), {
                mensaje: nuevaNotaSup,
                estado: notaSupEstado,
                autor: currentUser.name || currentUser.email,
                fecha: new Date().getTime(),
                createdAt: serverTimestamp()
            });
            setNuevaNotaSup('');
        } catch(error) {
            console.error('Error adding nota supervision', error);
        }
    };

    const handleToggleNotaEstado = async (notaId, estadoActual) => {
        const nuevoEstado = estadoActual === 'pendiente' ? 'resuelto' : 'pendiente';
        try {
            await updateDoc(doc(db, 'notas_supervision', notaId), { estado: nuevoEstado });
        } catch(error) {
            console.error('Error toggling estado', error);
        }
    };

    const handleIniciarTurno = async () => {
        try {
            const todayStr = new Date().toISOString().slice(0, 10);
            await addDoc(collection(db, 'asistencia_logs'), {
                email: currentUser.email,
                nombre_empleado: currentUser.name || currentUser.displayName || currentUser.email,
                hora_entrada: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                hora_salida: null,
                fecha_texto: todayStr,
                createdAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error al iniciar turno", error);
        }
    };

    const handleFinalizarTurno = async () => {
        if (!asistenciaActiva || !asistenciaActiva.id) return;
        try {
            await updateDoc(doc(db, 'asistencia_logs', asistenciaActiva.id), {
                hora_salida: new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
            });
        } catch (error) {
            console.error("Error al finalizar turno", error);
        }
    };

    const handleVerificarTurnoGlobal = async (turno) => {
        const asis = asistenciasHoyGlobal.find(a => a.email === turno.email);
        
        const { value: nota } = await Swal.fire({
            title: 'Validar Turno',
            input: 'text',
            inputLabel: 'Nota de Incidencia (Opcional)',
            inputPlaceholder: 'Ej: Llegó 15 min tarde',
            showCancelButton: true,
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b',
            confirmButtonText: 'Validar',
            cancelButtonText: 'Cancelar'
        });

        if (nota === undefined) return;

        const [h_inicio, m_inicio] = (turno.hora_inicio || '10:00').split(':').map(Number);
        const now = new Date();
        const mins_inicio = h_inicio * 60 + m_inicio;
        const mins_now = now.getHours() * 60 + now.getMinutes();
        const isRetardo = (mins_now - mins_inicio) > 15;

        try {
            if (isRetardo && turno.id) {
                await updateDoc(doc(db, 'personal', turno.id), {
                    expediente_retardos: arrayUnion({
                        tipo: 'Retardo',
                        fecha: new Date().toISOString().slice(0, 10),
                        hora_inicio: turno.hora_inicio || '10:00',
                        hora_verificacion: now.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
                        nota: nota || 'Marcado con retraso'
                    })
                });
            }

            if (asis) {
                await updateDoc(doc(db, 'asistencia_logs', asis.id), { cumplido: true, nota_incidencia: nota || (isRetardo ? 'Retardo' : '') });
            } else {
                const todayStr = new Date().toISOString().slice(0, 10);
                await addDoc(collection(db, 'asistencia_logs'), {
                    email: turno.email,
                    nombre_empleado: turno.nombre || turno.email,
                    hora_entrada: 'Verificado Manual',
                    hora_salida: 'Verificado Manual',
                    fecha_texto: todayStr,
                    cumplido: true,
                    nota_incidencia: nota || (isRetardo ? 'Retardo' : ''),
                    createdAt: serverTimestamp()
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const renderEstadoEquipo = () => {
        if (!canSeeMuro) return null;
        return (
            <div className="bg-slate-800 rounded-xl p-4 sm:p-6 border border-slate-700 shadow-xl mt-6">
                <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2"><Users size={20} className="text-emerald-500" /> Estado del Equipo (Turnos Hoy)</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-slate-300">
                        <thead className="bg-slate-900/50 text-slate-400 uppercase text-xs">
                            <tr>
                                <th className="px-4 py-3">Empleado</th>
                                <th className="px-4 py-3">Rango Horario</th>
                                <th className="px-4 py-3">Lunch</th>
                                <th className="px-4 py-3 text-center">Estado Asistencia</th>
                                <th className="px-4 py-3 text-center">Verificación</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {turnosHoyGlobal.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-6 text-slate-500">No hay turnos asignados para hoy</td></tr>
                            ) : turnosHoyGlobal.map(turno => {
                                const asis = asistenciasHoyGlobal.find(a => a.email === turno.email);
                                const cumplido = asis?.cumplido;
                                return (
                                    <tr key={turno.id} className="hover:bg-slate-700/30">
                                        <td className="px-4 py-3 font-medium text-white">{turno.nombre || turno.email}</td>
                                        <td className="px-4 py-3 text-slate-400">{turno.horario}</td>
                                        <td className="px-4 py-3 text-amber-400">{turno.lunch}</td>
                                        <td className="px-4 py-3 text-center">
                                            {asis ? (
                                                <div className="flex flex-col items-center gap-1">
                                                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-md font-bold">Activo / Presente</span>
                                                    {asis.nota_incidencia && <span className="text-[10px] text-amber-500 italic max-w-[120px] truncate" title={asis.nota_incidencia}>Nota: {asis.nota_incidencia}</span>}
                                                </div>
                                            ) : (
                                                <span className="text-xs bg-slate-700 text-slate-400 px-2 py-1 rounded-md">Sin Iniciar</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {cumplido ? (
                                                <span className="flex items-center justify-center gap-1 text-emerald-500 font-bold text-xs"><CheckCircle size={14}/> Cumplido</span>
                                            ) : (
                                                <button onClick={() => handleVerificarTurnoGlobal(turno)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1 rounded text-xs transition-colors flex items-center justify-center gap-1 mx-auto">
                                                    <CheckCircle size={14}/> Validar
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // EJECUTAMOS EL HOOK
    const { data, loading, refetch } = useDashboardData();

    const chartData = data.tableData ? ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((mes, idx) => {
        const ing = data.tableData.totalIngresos[idx] || 0;
        const gast = data.tableData.totalGastos[idx] || 0;
        const utMes = ing - gast;
        const fondo = data.tableData.fondoReserva?.[idx] || 0;
        const pctGasto = ing > 0 ? Number(((gast / ing) * 100).toFixed(1)) : 0;
        
        return {
            name: mes,
            Ingresos: ing,
            Gastos: gast,
            Utilidad: utMes > 0 ? utMes - fondo : utMes,
            PctGasto: pctGasto
        };
    }) : [];

    // Datos estáticos eliminados según requerimiento estricto.

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // DASHBOARD GESTIÓN DE CUENTAS
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    if (isGC) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-cyan-950/20 to-slate-900 space-y-6 text-slate-100">


                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 flex items-center gap-3 flex-wrap">
                            <span className="bg-cyan-500/20 text-cyan-400 p-2 rounded-xl"><Users size={24} /></span>
                            Panel de Gestión de Cuentas
                            {cantCuentasPausadas > 0 && (
                                <span className="bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5 animate-pulse shadow-lg">
                                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                    {cantCuentasPausadas} Cuentas Pausadas / Reportadas
                                </span>
                            )}
                        </h1>
                        <p className="text-slate-400 text-sm">Operaciones, stock y finanzas del departamento</p>
                    </div>
                </div>

                {/* Bienvenida + Mi Turno */}
                <div className="bg-gradient-to-r from-cyan-900/40 to-slate-800 rounded-xl p-4 sm:p-6 border border-cyan-500/20 shadow-lg">
                    <h2 className="text-xl sm:text-2xl font-bold text-cyan-400 mb-2">¡Bienvenida, {currentUser?.name || currentUser?.email}!</h2>
                    <p className="text-slate-300 text-sm">Gestión de Cuentas — {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                    {miTurnoHoy && (
                        <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <Clock className="text-cyan-400" size={24} />
                                <div>
                                    <p className="text-xs text-slate-400 font-bold uppercase">Mi Turno de Hoy</p>
                                    <p className="text-lg text-white font-mono">{miTurnoHoy.hora_inicio || '--:--'} → {miTurnoHoy.hora_fin || '--:--'}</p>
                                    <p className="text-xs text-amber-400">Lunch: {miTurnoHoy.inicio_almuerzo} - {miTurnoHoy.fin_almuerzo}</p>
                                    {(miTurnoHoy.descansos || []).map((ds, i) => (
                                        <p key={i} className="text-xs text-cyan-400">☕ B{i+1}: {ds.inicio} - {ds.fin}</p>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-3">
                                {!asistenciaActiva ? (
                                    <button onClick={handleIniciarTurno} className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 text-white px-5 py-2 rounded-lg font-medium transition-colors text-sm">
                                        <PlayCircle size={16} /> Iniciar Turno
                                    </button>
                                ) : asistenciaActiva.hora_salida ? (
                                    <div className="flex items-center gap-2 bg-slate-800 text-slate-400 px-5 py-2 rounded-lg border border-slate-700 text-sm">Finalizado a las {asistenciaActiva.hora_salida}</div>
                                ) : (
                                    <button onClick={handleFinalizarTurno} className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-5 py-2 rounded-lg font-medium transition-colors text-sm">
                                        <StopCircle size={16} /> Finalizar Turno
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {!miTurnoHoy && (
                        <div className="mt-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                            <p className="text-slate-400 flex items-center gap-2 text-sm"><Clock size={16} /> No tienes turno asignado para hoy.</p>
                        </div>
                    )}
                </div>

                {/* Alertas de Stock Bajo */}
                {stockBajo.length > 0 && (
                    <div className="bg-red-900/20 border border-red-500/40 rounded-xl p-4">
                        <h3 className="text-red-400 font-bold flex items-center gap-2 mb-3">
                            <AlertTriangle size={18} /> Plataformas con Stock Bajo — Requieren Reposición
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {stockBajo.map(s => (
                                <div key={s.id} onClick={() => navigate('/inventario')} className="cursor-pointer bg-red-900/30 border border-red-500/30 rounded-lg p-3 hover:bg-red-900/50 transition-colors">
                                    <p className="text-white font-semibold text-sm">{s.plataforma || s.nombre || s.id}</p>
                                    <p className="text-red-400 text-xs font-mono mt-1">{s.unidades ?? 0} unid. / mín. {s.punto_de_reorden ?? 5}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* ══ BLOQUE 1: Estado del Equipo Hoy — Gestión de Cuentas ══ */}
                <div className="bg-slate-800 rounded-xl border border-cyan-700/30 shadow-lg">
                    <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
                        <h3 className="text-base font-bold text-white flex items-center gap-2">
                            <Users size={18} className="text-cyan-400" /> Estado del Equipo Hoy — Gestión de Cuentas
                        </h3>
                        <span className="text-xs bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full font-bold">
                            {personalGCDep.length > 0 ? `${personalGCDep.length} activas hoy` : 'Sin turno hoy'}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-slate-300">
                            <thead className="bg-slate-900/60 text-cyan-600 uppercase text-[10px] tracking-wider">
                                <tr>
                                    <th className="px-5 py-3 text-left">Empleado/a</th>
                                    <th className="px-5 py-3 text-center">Turno</th>
                                    <th className="px-5 py-3 text-center">🍽 Lunch</th>
                                    <th className="px-5 py-3 text-center">☕ Descansos</th>
                                    <th className="px-5 py-3 text-center">Asistencia</th>
                                    <th className="px-5 py-3 text-center">Verificar</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/40">
                                {personalGCDep.length === 0 ? (
                                    <tr><td colSpan="6" className="text-center py-8 text-slate-500 text-sm">Ningún miembro del equipo GC tiene turno programado para hoy.</td></tr>
                                ) : personalGCDep.map(emp => {
                                    const asis = asistenciasHoyGlobal.find(a => a.email === emp.email);
                                    const cumplido = asis?.cumplido;
                                    return (
                                        <tr key={emp.id} className="hover:bg-cyan-900/10 transition-colors">
                                            <td className="px-5 py-3 font-semibold text-white">{emp.nombre}</td>
                                            <td className="px-5 py-3 font-mono text-cyan-400 text-center">{emp.horario}</td>
                                            <td className="px-5 py-3 font-mono text-amber-400 text-center">{emp.lunch}</td>
                                            <td className="px-5 py-3">
                                                {emp.descansos && emp.descansos.length > 0 ? (
                                                    <div className="space-y-0.5">
                                                        {emp.descansos.map((d, i) => (
                                                            <span key={i} className="flex items-center gap-1 text-[11px] text-cyan-300 font-mono">
                                                                <span className="text-cyan-600 font-bold">B{i+1}</span> {d.inicio} → {d.fin}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : <span className="text-slate-600 text-xs">—</span>}
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                {asis ? (
                                                    <div className="flex flex-col items-center gap-0.5">
                                                        <span className="text-[11px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-bold">Presente</span>
                                                        {asis.nota_incidencia && <span className="text-[10px] text-amber-400 italic max-w-[100px] truncate" title={asis.nota_incidencia}>⚠ {asis.nota_incidencia}</span>}
                                                    </div>
                                                ) : (
                                                    <span className="text-[11px] bg-slate-700 text-slate-400 px-2 py-0.5 rounded">Sin iniciar</span>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-center">
                                                {cumplido ? (
                                                    <span className="flex items-center justify-center gap-1 text-emerald-500 font-bold text-xs">
                                                        <CheckCircle size={13} /> Cumplido
                                                    </span>
                                                ) : (
                                                    <button onClick={() => handleVerificarTurnoGlobal(emp)}
                                                        className="bg-cyan-700 hover:bg-cyan-600 text-white px-3 py-1 rounded text-xs transition-colors flex items-center gap-1 mx-auto">
                                                        <CheckCircle size={13} /> Check
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ══ BLOQUE 3: Horario Maestro Semanal — Gestión de Cuentas ══ */}
                <div className="bg-slate-800 rounded-xl border border-cyan-700/30 shadow-xl mt-6">
                    <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <CalendarDays size={20} className="text-cyan-400" /> Horario Maestro Semanal — Gestión de Cuentas
                        </h3>
                        <span className="text-xs bg-cyan-500/10 text-cyan-500 px-3 py-1 rounded-full font-bold">{personalGCSemanal.length} empleados</span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs text-slate-300">
                            <thead className="bg-slate-900/60 text-cyan-600 uppercase text-[10px] tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 text-left">Empleada</th>
                                    {[{label:'Lun',idx:1},{label:'Mar',idx:2},{label:'Mié',idx:3},{label:'Jue',idx:4},{label:'Vie',idx:5},{label:'Sáb',idx:6},{label:'Dom',idx:0}].map(({label,idx}) => {
                                        const isToday = idx === new Date().getDay();
                                        return (
                                            <th key={idx} className={`px-3 py-3 text-center ${isToday ? 'text-cyan-400' : ''}`}>
                                                {label}{isToday && <span className="ml-1 text-[8px] bg-cyan-500/20 text-cyan-300 px-1 py-0.5 rounded-full">HOY</span>}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700/30">
                                {personalGCSemanal.length === 0 ? (
                                    <tr><td colSpan="8" className="text-center py-6 text-slate-500">Sin empleadas configuradas.</td></tr>
                                ) : personalGCSemanal.map(emp => (
                                    <tr key={emp.id} className="hover:bg-cyan-900/10 transition-colors">
                                        <td className="px-4 py-2 font-semibold text-white">{emp.nombre}</td>
                                        {[1,2,3,4,5,6,0].map(dayIdx => {
                                            const h = emp.horario_maestro?.[dayIdx];
                                            const isToday = dayIdx === new Date().getDay();
                                            return (
                                                <td key={dayIdx} className={`px-3 py-2 text-center align-top ${isToday ? 'bg-cyan-500/5' : ''}`}>
                                                    {h && h.activo ? (
                                                        <div className="space-y-0.5">
                                                            <div className="text-cyan-400 font-mono font-bold">{h.hora_inicio}–{h.hora_fin}</div>
                                                            <div className="text-amber-400 text-[9px]">🍽 {h.inicio_almuerzo}–{h.fin_almuerzo}</div>
                                                            {(h.descansos || []).map((ds, di) => (
                                                                <div key={di} className="text-cyan-300 text-[9px]">☕B{di+1} {ds.inicio}–{ds.fin}</div>
                                                            ))}
                                                        </div>
                                                    ) : <span className="text-slate-700">—</span>}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* ══ BLOQUE 4: Registro de Compras y Renovaciones ══ */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    {/* Formulario Registro Compra/Renovación */}
                    <div className="bg-slate-800 rounded-xl border border-emerald-700/40 shadow-lg p-6">
                        <h3 className="text-base font-bold text-emerald-400 flex items-center gap-2 mb-4">
                            <CreditCard size={18} className="text-emerald-400" /> 💰 Registro de Compras y Renovaciones
                        </h3>
                        <form onSubmit={handleGCGasto} className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs text-slate-400 mb-1 block">Plataforma *</label>
                                    <select value={gcGastoPlataforma} onChange={e => setGcGastoPlataforma(e.target.value)} required
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none">
                                        <option value="">Seleccionar...</option>
                                        {configServicios.map(s => <option key={s.id} value={s.nombre}>{s.nombre}</option>)}
                                        <option value="Otro">Otro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 mb-1 block">Monto ($) *</label>
                                    <input type="text" inputMode="decimal" value={gcGastoMonto} onChange={e => setGcGastoMonto(e.target.value)} required placeholder="Ej. 10.000,05"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" />
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 mb-1 block">Método de Pago</label>
                                    <select value={gcGastoMetodo} onChange={e => setGcGastoMetodo(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none">
                                        <option>Binance Sub-cuenta</option><option>Zelle</option><option>Pago Móvil</option><option>Efectivo</option><option>Transferencia</option><option>Tarjeta</option><option>Paypal</option><option>Otro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-xs text-slate-400 mb-1 block">Proveedor</label>
                                    <input type="text" value={gcGastoProveedor} onChange={e => setGcGastoProveedor(e.target.value)} placeholder="Nombre del proveedor"
                                        className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-slate-400 mb-1 block">Descripción</label>
                                <input type="text" value={gcGastoDesc} onChange={e => setGcGastoDesc(e.target.value)} placeholder="Ej: Renovación Netflix 4K x3 cuentas"
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none" />
                            </div>
                            <button type="submit" disabled={!gcGastoPlataforma || !gcGastoMonto}
                                className="w-full bg-cyan-600 hover:bg-cyan-500 text-white py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                <DollarSign size={16} /> Registrar Compra
                            </button>
                        </form>
                    </div>

                    {/* Histórico de Gastos GC */}
                    <div className="bg-slate-800 rounded-xl border border-cyan-700/30 shadow-lg flex flex-col" style={{maxHeight:'420px'}}>
                        <div className="px-6 py-4 border-b border-slate-700">
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <TrendingUp size={18} className="text-cyan-400" /> Historial de Compras
                            </h3>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-2">
                            {gastosGC.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center pt-6">Sin registros aún.</p>
                            ) : gastosGC.map(g => (
                                <div key={g.id} className="flex items-center justify-between p-3 bg-slate-900/60 rounded-lg border border-slate-700">
                                    <div>
                                        <p className="text-sm font-semibold text-white">{g.plataforma || g.descripcion}</p>
                                        <p className="text-[11px] text-slate-500">{g.metodo_pago} • {g.proveedor || 'Sin proveedor'} • {g.registrado_por}</p>
                                    </div>
                                    <span className="text-cyan-400 font-mono font-bold text-sm">${Number(g.amount || g.monto || 0).toFixed(2)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ══ BLOQUE 2: Bitácora de Gestión Técnica ══ */}
                <div className="bg-slate-800 rounded-xl border border-cyan-600/40 shadow-lg mt-6">
                    <div className="px-6 py-4 border-b border-cyan-700/40 bg-cyan-950/20">
                        <h3 className="text-base font-bold text-cyan-300 flex items-center gap-2">
                            <MessageSquare size={18} className="text-cyan-400" /> Bitácora de Gestión Técnica
                        </h3>
                        <p className="text-[11px] text-cyan-700 mt-0.5">Renovaciones pendientes, incidencias técnicas y reportes de proveedores — exclusivo dpto. GC</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 divide-y lg:divide-y-0 lg:divide-x divide-slate-700">
                        {/* Historial */}
                        <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
                            {notasGC.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center pt-6">Sin entradas. Añade la primera nota.</p>
                            ) : notasGC.map(nota => {
                                const isPendiente = nota.estado === 'pendiente';
                                return (
                                    <div key={nota.id} className={`p-3 rounded-xl border transition-all ${isPendiente ? 'bg-amber-500/5 border-amber-500/30' : 'bg-slate-900/50 border-slate-700/40 opacity-70'}`}>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => handleToggleNotaGC(nota.id, nota.estado)}
                                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all ${isPendiente ? 'bg-amber-500/20 text-amber-400 border-amber-500/40 hover:bg-amber-500/40' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'}`}>
                                                    {isPendiente ? '🔶 Pendiente' : '✅ Resuelto'}
                                                </button>
                                                <span className="text-xs font-bold text-cyan-300">{nota.autor}</span>
                                            </div>
                                            <span className="text-[10px] text-slate-500">{new Date(nota.fecha).toLocaleString('es-ES', {hour:'2-digit',minute:'2-digit',day:'2-digit',month:'2-digit'})}</span>
                                        </div>
                                        <p className="text-sm text-slate-300 leading-snug">{nota.mensaje}</p>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Formulario */}
                        <div className="p-4 flex flex-col gap-3">
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setNotaGCEstado('pendiente')} className={`flex-1 text-xs py-2 rounded-lg font-medium border transition-colors ${notaGCEstado === 'pendiente' ? 'bg-amber-600 border-amber-500 text-white' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white'}`}>
                                    🔶 Pendiente
                                </button>
                                <button type="button" onClick={() => setNotaGCEstado('resuelto')} className={`flex-1 text-xs py-2 rounded-lg font-medium border transition-colors ${notaGCEstado === 'resuelto' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white'}`}>
                                    ✅ Resuelto / Info
                                </button>
                            </div>
                            <form onSubmit={handleGCNota} className="flex flex-col gap-2 flex-1">
                                <textarea value={nuevaNotaGC} onChange={e => setNuevaNotaGC(e.target.value)}
                                    placeholder="Ej: Proveedor X no responde — renovación pendiente de Netflix..."
                                    rows={5}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:border-cyan-500 focus:outline-none resize-none" />
                                <button type="submit" disabled={!nuevaNotaGC.trim()}
                                    className="bg-cyan-600 hover:bg-cyan-500 text-white py-2 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                                    <Send size={15} /> Publicar en Gestión
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // DASHBOARD MARKETING LEAN
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    if (isMarketing) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-fuchsia-950/20 to-slate-900 space-y-8 text-slate-100">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 flex items-center gap-3">
                            <span className="bg-fuchsia-500/20 text-fuchsia-400 p-2 rounded-xl"><Megaphone size={24} /></span>
                            Centro de Estrategia y Difusión
                        </h1>
                        <p className="text-slate-400 text-sm">Dashboard de Marketing y Contenido</p>
                    </div>
                </div>

                <div className="bg-gradient-to-r from-fuchsia-900/30 to-slate-800 rounded-xl p-4 sm:p-6 border border-fuchsia-500/20 shadow-lg mb-8">
                    <h2 className="text-xl sm:text-2xl font-bold text-fuchsia-400 mb-1">¡Bienvenida, {currentUser?.name || currentUser?.email}!</h2>
                    <p className="text-slate-300 text-sm">Estrategia de Ventas — {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* BLOQUE 1: MONITOR DE EMPUJE COMERCIAL */}
                    <div className="bg-slate-800 rounded-xl border border-fuchsia-700/30 shadow-lg p-6">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
                            <TrendingUp size={20} className="text-fuchsia-400" /> 🔥 Promocionar con Prioridad
                        </h3>
                        <div className="space-y-4">
                            {stockMarketing.length === 0 ? (
                                <p className="text-sm text-slate-500">Cargando datos de stock...</p>
                            ) : stockMarketing.map((item, i) => (
                                <div key={item.id} className="flex items-center justify-between p-4 bg-slate-900/60 rounded-xl border border-slate-700">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-fuchsia-500/20 flex items-center justify-center text-fuchsia-400 font-bold text-sm">
                                            #{i + 1}
                                        </div>
                                        <p className="font-semibold text-white">{item.plataforma}</p>
                                    </div>
                                    <span className="text-fuchsia-400 font-mono font-bold text-sm bg-fuchsia-500/10 px-3 py-1 rounded-full">
                                        {item.unidades} disp.
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* BLOQUE 2: AGENDA DE CONTENIDO (CHECKLIST) */}
                    <div className="bg-slate-800 rounded-xl border border-fuchsia-700/30 shadow-lg p-6">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
                            <CheckSquare size={20} className="text-fuchsia-400" /> Agenda de Contenido (Hoy)
                        </h3>
                        <div className="space-y-3">
                            {[
                                { key: 'envio_revendedores', label: 'Envío a Revendedores (Mañana/Tarde/Noche)' },
                                { key: 'produccion_tiktok', label: 'Producción TikTok' },
                                { key: 'interaccion_grupos', label: 'Interacción en Grupos' }
                            ].map(task => {
                                const isChecked = marketingChecklist[task.key];
                                return (
                                    <label key={task.key} className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${isChecked ? 'bg-fuchsia-900/20 border-fuchsia-500/40' : 'bg-slate-900/60 border-slate-700 hover:border-fuchsia-500/30'}`}>
                                        <input type="checkbox" checked={isChecked} onChange={() => handleToggleMarketingCheck(task.key)} className="w-5 h-5 rounded border-slate-600 text-fuchsia-500 focus:ring-fuchsia-500/20 bg-slate-800" />
                                        <span className={`text-sm font-medium ${isChecked ? 'text-fuchsia-300 line-through opacity-70' : 'text-slate-200'}`}>
                                            {task.label}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>

                    {/* BLOQUE 3: BITÁCORA DE ESTRATEGIA */}
                    <div className="bg-slate-800 rounded-xl border border-fuchsia-600/40 shadow-lg flex flex-col h-[500px]">
                        <div className="px-6 py-4 border-b border-fuchsia-700/40 bg-fuchsia-950/20">
                            <h3 className="text-lg font-bold text-fuchsia-300 flex items-center gap-2">
                                <MessageSquare size={20} className="text-fuchsia-400" /> Instrucciones de Campaña
                            </h3>
                            <p className="text-[11px] text-fuchsia-500/70 mt-1">Copys y estrategias designadas por Administración</p>
                        </div>
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                            {notasMarketing.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center pt-6">Sin instrucciones activas.</p>
                            ) : notasMarketing.map(nota => {
                                const isPendiente = nota.estado === 'pendiente';
                                return (
                                    <div key={nota.id} className={`p-4 rounded-xl border transition-all ${isPendiente ? 'bg-amber-500/5 border-amber-500/30' : 'bg-slate-900/50 border-slate-700/40 opacity-70'}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => handleToggleNotaMarketing(nota.id, nota.estado)}
                                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all ${isPendiente ? 'bg-amber-500/20 text-amber-400 border-amber-500/40 hover:bg-amber-500/40' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40'}`}>
                                                    {isPendiente ? '🔶 Pendiente' : '✅ Revisado'}
                                                </button>
                                                <span className="text-xs font-bold text-fuchsia-300">{nota.autor}</span>
                                            </div>
                                            <span className="text-[10px] text-slate-500">{new Date(nota.fecha).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                                        </div>
                                        <p className="text-sm text-slate-300 leading-snug whitespace-pre-wrap">{nota.mensaje}</p>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Formulario (Opcional, pero se mantiene para que Mktg también pueda dejar feedback) */}
                        <div className="p-4 border-t border-slate-700/50 flex flex-col gap-2">
                            <form onSubmit={handleMarketingNota} className="flex flex-col gap-2">
                                <textarea value={nuevaNotaMarketing} onChange={e => setNuevaNotaMarketing(e.target.value)}
                                    placeholder="Añadir nota o feedback sobre campaña..."
                                    rows={2}
                                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:border-fuchsia-500 focus:outline-none resize-none" />
                                <div className="flex gap-2">
                                    <button type="submit" disabled={!nuevaNotaMarketing.trim()}
                                        className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-500 text-white py-2 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm">
                                        <Send size={15} /> Publicar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        );
    }


    if (!isAdminOrGerente) {
        const depLabel = (isSupervisor || isATC || isSupervisorATC) ? 'Atención al Cliente' : (userDep || 'Tu Departamento');

        return (
            <div className="min-h-screen bg-slate-900 space-y-6 text-slate-100">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-white tracking-tight m-0">
                            {canSeeMuro ? 'Panel de Supervisión' : 'Panel de Trabajo Diario'}
                        </h1>
                        <p className="text-sm text-slate-400 mt-1">
                            {canSeeMuro ? `Coordinación y control — ${depLabel}` : 'Operaciones y Seguimiento'}
                        </p>
                    </div>
                </div>

                {/* Fila superior: Bienvenida + Rutina */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-r from-emerald-900/40 to-slate-800 rounded-xl p-4 sm:p-6 border border-emerald-500/20 shadow-lg col-span-1 lg:col-span-2">
                        <h2 className="text-xl sm:text-2xl font-bold text-emerald-400 mb-2">¡Bienvenido, {currentUser?.name || currentUser?.email}!</h2>
                        <p className="text-slate-300 text-sm">Aquí puedes revisar tu turno, rutina y coordinar con tu equipo.</p>

                        {miTurnoHoy && (
                            <div className="mt-4 p-4 bg-slate-900/50 rounded-lg border border-slate-700 flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <Clock className="text-indigo-400" size={24} />
                                    <div>
                                        <p className="text-xs text-slate-400 font-bold uppercase">Mi Turno de Hoy</p>
                                        <p className="text-lg text-white font-mono">{miTurnoHoy.hora_inicio || '--:--'} → {miTurnoHoy.hora_fin || '--:--'}</p>
                                        <p className="text-xs text-amber-400">Lunch: {miTurnoHoy.inicio_almuerzo} - {miTurnoHoy.fin_almuerzo} · {miTurnoHoy.minutos_libres} min libres</p>
                                    </div>
                                </div>
                                <div className="flex gap-3">
                                    {!asistenciaActiva ? (
                                        <button onClick={handleIniciarTurno} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2 rounded-lg font-medium transition-colors text-sm">
                                            <PlayCircle size={16} /> Iniciar Turno
                                        </button>
                                    ) : asistenciaActiva.hora_salida ? (
                                        <div className="flex items-center gap-2 bg-slate-800 text-slate-400 px-5 py-2 rounded-lg font-medium border border-slate-700 text-sm">
                                            Finalizado a las {asistenciaActiva.hora_salida}
                                        </div>
                                    ) : (
                                        <button onClick={handleFinalizarTurno} className="flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white px-5 py-2 rounded-lg font-medium transition-colors text-sm">
                                            <StopCircle size={16} /> Finalizar Turno
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        {!miTurnoHoy && (
                            <div className="mt-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                                <p className="text-slate-400 flex items-center gap-2 text-sm"><Clock size={16} /> No tienes turnos asignados para hoy.</p>
                            </div>
                        )}
                    </div>

                    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-lg">
                        <h4 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                            <CheckSquare className="text-indigo-400" size={18} /> Mi Rutina
                        </h4>
                        <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                            {rutinasActivas.length === 0 ? (
                                <p className="text-sm text-slate-500">Sin rutinas configuradas para tu departamento.</p>
                            ) : (
                                rutinasActivas.map(r => (
                                    <label key={r.id} className="flex items-start gap-3 text-slate-300 text-sm cursor-pointer hover:text-emerald-400 transition-colors p-2 bg-slate-900/30 rounded-lg border border-slate-700/50 hover:border-slate-600">
                                        <input type="checkbox" checked={!!rutinasProgreso[r.id]} onChange={() => handleToggleRoutineCheck(r.id)} className="mt-0.5 rounded bg-slate-700 border-slate-600 text-emerald-500 focus:ring-0" />
                                        <span className={`leading-snug ${rutinasProgreso[r.id] ? 'line-through text-slate-500' : ''}`}>{r.tarea}</span>
                                    </label>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* CRONOGRAMA DEPARTAMENTAL — Solo para supervisores */}
                {canSeeMuro && (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl">
                        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    <Users size={20} className="text-cyan-400" />
                                    Estado del Equipo Hoy — <span className="text-cyan-400">{depLabel}</span>
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">Cronograma de turnos, lunch y minutos libres</p>
                            </div>
                            <span className="text-xs bg-cyan-500/20 text-cyan-400 px-3 py-1 rounded-full font-bold">{personalDep.length} activos hoy</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-slate-300">
                                <thead className="bg-slate-900/60 text-slate-500 uppercase text-[11px] tracking-wider">
                                    <tr>
                                        <th className="px-5 py-3">Empleado</th>
                                        <th className="px-5 py-3">Turno</th>
                                        <th className="px-5 py-3">🍽 Lunch</th>
                                        <th className="px-5 py-3">☕ Descansos</th>
                                        <th className="px-5 py-3 text-center">Asistencia</th>
                                        <th className="px-5 py-3 text-center">Verificar</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/40">
                                    {personalDep.length === 0 ? (
                                        <tr><td colSpan="6" className="text-center py-8 text-slate-500">Sin empleados activos hoy en este departamento.</td></tr>
                                    ) : personalDep.map(turno => {
                                        const asis = asistenciasHoyGlobal.find(a => a.email === turno.email);
                                        const cumplido = asis?.cumplido;
                                        return (
                                            <tr key={turno.id} className="hover:bg-slate-700/25 transition-colors">
                                                <td className="px-5 py-3 font-semibold text-white">{turno.nombre}</td>
                                                <td className="px-5 py-3 font-mono text-emerald-400">{turno.horario}</td>
                                                <td className="px-5 py-3 font-mono text-amber-400">{turno.lunch}</td>
                                                <td className="px-5 py-3">
                                                    {turno.descansos && turno.descansos.length > 0 ? (
                                                        <div className="space-y-1">
                                                            {turno.descansos.map((d, i) => (
                                                                <span key={i} className="flex items-center gap-1 text-[11px] text-cyan-300 font-mono">
                                                                    <span className="text-cyan-600 font-bold">B{i+1}</span> {d.inicio} → {d.fin}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-[11px] text-slate-600 italic">—</span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-center">
                                                    {asis ? (
                                                        <div className="flex flex-col items-center gap-0.5">
                                                            <span className="text-[11px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-bold">Presente</span>
                                                            {asis.nota_incidencia && <span className="text-[10px] text-amber-400 italic max-w-[110px] truncate" title={asis.nota_incidencia}>⚠ {asis.nota_incidencia}</span>}
                                                        </div>
                                                    ) : (
                                                        <span className="text-[11px] bg-slate-700 text-slate-400 px-2 py-0.5 rounded">Sin iniciar</span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3 text-center">
                                                    {cumplido ? (
                                                        <span className="flex items-center justify-center gap-1 text-emerald-500 font-bold text-xs">
                                                            <CheckCircle size={14} /> Cumplido
                                                        </span>
                                                    ) : (
                                                        <button onClick={() => handleVerificarTurnoGlobal(turno)} className="bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1 rounded text-xs transition-colors flex items-center gap-1 mx-auto">
                                                            <CheckCircle size={13} /> Check
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* BITÁCORA DE COORDINACIÓN */}
                {canSeeMuro && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Historial notas */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg flex flex-col" style={{minHeight: '420px'}}>
                            <div className="px-6 py-4 border-b border-slate-700">
                                <h3 className="text-base font-bold text-white flex items-center gap-2">
                                    <MessageSquare size={18} className="text-violet-400" /> Bitácora de Coordinación
                                </h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">Mensajes entre supervisores — visibles para todo el equipo de mando</p>
                            </div>
                            <div className="flex-1 overflow-y-auto p-4 space-y-2">
                                {notasSupervision.length === 0 ? (
                                    <p className="text-sm text-slate-500 text-center pt-8">Sin entradas en la bitácora.</p>
                                ) : notasSupervision.map(nota => {
                                    const isPendiente = nota.estado === 'pendiente';
                                    return (
                                        <div key={nota.id} className={`p-3 rounded-xl border transition-all ${isPendiente ? 'bg-amber-500/5 border-amber-500/30' : 'bg-slate-900/50 border-slate-700/40 opacity-70'}`}>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={() => handleToggleNotaEstado(nota.id, nota.estado)}
                                                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full border transition-all ${isPendiente ? 'bg-amber-500/20 text-amber-400 border-amber-500/40 hover:bg-amber-500/40' : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/40'}`}
                                                        title="Cambiar estado"
                                                    >
                                                        {isPendiente ? '🔶 Pendiente' : '✅ Resuelto'}
                                                    </button>
                                                    <span className="text-xs font-bold text-violet-300">{nota.autor}</span>
                                                </div>
                                                <span className="text-[10px] text-slate-500">{new Date(nota.fecha).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                                            </div>
                                            <p className="text-sm text-slate-300 leading-snug">{nota.mensaje}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Formulario nueva nota */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-lg p-6 flex flex-col gap-4">
                            <div>
                                <h3 className="text-base font-bold text-white flex items-center gap-2">
                                    <Send size={16} className="text-violet-400" /> Nueva Entrada
                                </h3>
                                <p className="text-[11px] text-slate-500 mt-0.5">Deja un mensaje para el siguiente turno o reporta una novedad</p>
                            </div>

                            <div className="flex gap-2">
                                <button type="button" onClick={() => setNotaSupEstado('pendiente')} className={`flex-1 text-xs py-2 rounded-lg font-medium border transition-colors ${notaSupEstado === 'pendiente' ? 'bg-amber-600 border-amber-500 text-white' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white'}`}>
                                    🔶 Pendiente
                                </button>
                                <button type="button" onClick={() => setNotaSupEstado('resuelto')} className={`flex-1 text-xs py-2 rounded-lg font-medium border transition-colors ${notaSupEstado === 'resuelto' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white'}`}>
                                    ✅ Resuelto / Info
                                </button>
                            </div>

                            <form onSubmit={handleAddNotaSupervision} className="flex flex-col gap-3 flex-1">
                                <textarea
                                    value={nuevaNotaSup}
                                    onChange={e => setNuevaNotaSup(e.target.value)}
                                    placeholder="Escribe tu mensaje para el equipo de supervisión..."
                                    rows={6}
                                    className="w-full flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm text-white focus:border-violet-500 focus:outline-none resize-none"
                                />
                                <button type="submit" disabled={!nuevaNotaSup.trim()} className="bg-violet-600 hover:bg-violet-500 text-white py-2.5 rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                                    <Send size={16} /> Publicar en Bitácora
                                </button>
                            </form>
                        </div>
                    </div>
                )}

                {/* Bitácora de Turno (solo si NO es supervisor) */}
                {!canSeeMuro && (
                    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 shadow-lg flex flex-col gap-4">
                        <div>
                            <h4 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                                <MessageSquare className="text-indigo-400" size={20} /> Bitácora de Turno
                            </h4>
                            <p className="text-[11px] text-slate-500">Instrucciones del equipo</p>
                        </div>
                        {/* Formulario de entrada */}
                        <div className="flex flex-col gap-2">
                            <div className="flex gap-2">
                                <button type="button" onClick={() => setNotaTipo('instruccion')} className={`flex-1 text-xs py-2 rounded-lg font-medium border transition-colors ${notaTipo === 'instruccion' ? 'bg-indigo-600 border-indigo-500 text-white' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white'}`}>
                                    📋 Instrucción
                                </button>
                                <button type="button" onClick={() => setNotaTipo('novedad')} className={`flex-1 text-xs py-2 rounded-lg font-medium border transition-colors ${notaTipo === 'novedad' ? 'bg-amber-600 border-amber-500 text-white' : 'bg-slate-900 border-slate-700 text-slate-400 hover:text-white'}`}>
                                    ⚠ Novedad
                                </button>
                            </div>
                            <form onSubmit={handleAddNota} className="flex gap-2">
                                <textarea
                                    value={nuevaNota}
                                    onChange={e => setNuevaNota(e.target.value)}
                                    placeholder={notaTipo === 'novedad' ? 'Reportar novedad...' : 'Escribe una instrucción para el equipo...'}
                                    rows={3}
                                    className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none resize-none"
                                />
                                <button type="submit" disabled={!nuevaNota.trim()} className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2 rounded-xl font-medium transition-colors disabled:opacity-50 self-end">
                                    <Send size={15} />
                                </button>
                            </form>
                        </div>
                        {/* Historial */}
                        <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
                            {notas.length === 0 ? (
                                <p className="text-sm text-slate-500 text-center pt-4">Sin entradas.</p>
                            ) : (
                                notas.map(nota => {
                                    const esNovedad = nota.tipo === 'novedad';
                                    return (
                                        <div key={nota.id} className={`p-3 rounded-lg border ${esNovedad ? 'bg-amber-500/5 border-amber-500/30' : 'bg-indigo-500/5 border-indigo-500/30'}`}>
                                            <div className="flex justify-between items-center mb-1">
                                                <div className="flex items-center gap-2">
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${esNovedad ? 'bg-amber-500/20 text-amber-400' : 'bg-indigo-500/20 text-indigo-400'}`}>
                                                        {esNovedad ? '⚠ Novedad' : '📋 Instrucción'}
                                                    </span>
                                                    <span className="text-xs font-bold text-emerald-400">{nota.autor}</span>
                                                </div>
                                                <span className="text-[10px] text-slate-500">{new Date(nota.fecha).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}</span>
                                            </div>
                                            <p className="text-sm text-slate-300">{nota.mensaje}</p>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                )}

                {/* HORARIO MAESTRO SEMANAL DEL DEPARTAMENTO */}
                {canSeeMuro && (
                    <div className="bg-slate-800 rounded-xl border border-slate-700 shadow-xl">
                        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                    <CalendarDays size={20} className="text-cyan-400" />
                                    Horario Maestro Semanal
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">Planificación completa de {depLabel} — semana actual</p>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs text-slate-300">
                                <thead className="bg-slate-900/60 text-slate-500 uppercase text-[10px] tracking-wider">
                                    <tr>
                                        <th className="px-4 py-3 text-left">Empleado</th>
                                        {[{label:'Lun',idx:1},{label:'Mar',idx:2},{label:'Mié',idx:3},{label:'Jue',idx:4},{label:'Vie',idx:5},{label:'Sáb',idx:6},{label:'Dom',idx:0}].map(({label, idx}) => {
                                            const isToday = idx === new Date().getDay();
                                            return (
                                                <th key={idx} className={`px-3 py-3 text-center ${isToday ? 'text-cyan-400' : ''}`}>
                                                    {label}{isToday && <span className="ml-1 text-[8px] bg-cyan-500/20 text-cyan-400 px-1 py-0.5 rounded-full">HOY</span>}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700/30">
                                    {personalSemanalDep.length === 0 ? (
                                        <tr><td colSpan="8" className="text-center py-6 text-slate-500">Sin empleados configurados.</td></tr>
                                    ) : personalSemanalDep.map(emp => (
                                        <tr key={emp.id} className="hover:bg-slate-700/20 transition-colors">
                                            <td className="px-4 py-2 font-semibold text-white">{emp.nombre}</td>
                                            {[1,2,3,4,5,6,0].map(dayIdx => {
                                                const h = emp.horario_maestro?.[dayIdx];
                                                const isToday = dayIdx === new Date().getDay();
                                                return (
                                                    <td key={dayIdx} className={`px-3 py-2 text-center align-top ${isToday ? 'bg-cyan-500/5' : ''}`}>
                                                        {h && h.activo ? (
                                                            <div className="space-y-1">
                                                                <div className="text-emerald-400 font-mono font-bold">{h.hora_inicio}–{h.hora_fin}</div>
                                                                <div className="text-amber-400 text-[9px]">🍽 {h.inicio_almuerzo}–{h.fin_almuerzo}</div>
                                                                {(h.descansos || []).map((ds, di) => (
                                                                    <div key={di} className="text-cyan-400 text-[9px]">☕B{di+1} {ds.inicio}–{ds.fin}</div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-slate-700 text-[10px]">—</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

            </div>
        );
    }




    return (
        <div className="min-h-screen bg-slate-900 space-y-6 text-slate-100">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight m-0">Dashboard</h1>
                    <p className="text-sm text-slate-400 mt-1">Resumen Ejecutivo y Estado de Operaciones</p>
                </div>
                {/* Botón de recarga conectado a 'refetch' */}
                <button onClick={refetch} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm transition-colors">
                    <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                    Actualizar Métricas
                </button>
            </div>

            {renderKPIModal()}
            {renderKpiBanner()}

            {/* SECCIÓN ESTRATÉGICA Y GOBERNANZA */}
            {isAdminOrGerente && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 animate-in fade-in duration-300">
                    {/* Panel 1: Cumplimiento de Equipo */}
                    <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700/60 flex flex-col justify-between shadow-xl">
                        <div>
                            <h3 className="text-white font-bold mb-4 flex items-center gap-2 text-lg">
                                <CheckSquare size={20} className="text-emerald-500" /> Cumplimiento de Equipo
                            </h3>

                            <div className="space-y-6">
                                {[
                                    { label: 'Checklist Supervisores', pct: calcularCumplimiento('Gerencia') },
                                    { label: 'Gestión de Cuentas', pct: calcularCumplimiento('Cuentas') },
                                    { label: 'Marketing', pct: calcularCumplimiento('Marketing') },
                                    { label: 'Atención al Cliente', pct: calcularCumplimiento('Atención al Cliente') }
                                ].map((depData, idx) => (
                                    <div key={idx}>
                                        <div className="flex justify-between text-sm mb-1">
                                            <span className="text-slate-300 font-medium">{depData.label}</span>
                                            <span className={`font-bold ${getTextColor(depData.pct)}`}>{depData.pct}%</span>
                                        </div>
                                        <div className="w-full bg-slate-700 rounded-full h-2.5">
                                            <div className={`h-2.5 rounded-full transition-all duration-500 ${getBarColor(depData.pct)}`} style={{ width: `${depData.pct}%` }}></div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* HISTORIAL DE INCUMPLIMIENTO (AUDITORÍA) */}
                            <div className="mt-6 border-t border-slate-700/60 pt-4">
                                <h4 className="text-xs font-bold text-amber-500 mb-2 flex items-center gap-1 uppercase tracking-wider">
                                    ⚠️ Alertas de Tareas Pendientes
                                </h4>
                                <div className="bg-slate-900/50 p-3 rounded-xl border border-slate-700/50 max-h-32 overflow-y-auto space-y-2">
                                    {historicoTareasPendientes.length === 0 ? (
                                        <p className="text-[11px] text-slate-500 italic text-center py-2 font-medium">No hay tareas pendientes históricas</p>
                                    ) : (
                                        historicoTareasPendientes.map((p, idx) => (
                                            <div key={idx} className="text-xs border-b border-slate-800 pb-1.5 last:border-0 last:pb-0 flex flex-col gap-0.5">
                                                <div className="flex justify-between items-center text-[10px] text-slate-400">
                                                    <span className="font-semibold text-slate-300 truncate max-w-[150px]">{p.usuario}</span>
                                                    <span className="font-mono text-[9px]">{p.fecha}</span>
                                                </div>
                                                <p className="text-slate-200 text-[11px] truncate" title={p.tarea}>{p.tarea}</p>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 border-t border-slate-700/60 pt-4">
                                <h4 className="text-sm font-bold text-slate-300 mb-2">Mi Rutina Gerencial</h4>
                                <div className="space-y-2">
                                    {rutinasGerencia.length === 0 ? <p className="text-xs text-slate-500">No hay rutinas configuradas</p> : rutinasGerencia.map(r => (
                                        <label key={r.id} className="flex items-center gap-2 text-slate-300 text-sm cursor-pointer hover:text-emerald-400 transition-colors font-medium">
                                            <input type="checkbox" className="rounded bg-slate-700 border-slate-600 text-emerald-500 focus:ring-0" />
                                            {r.tarea}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Panel 2: Bitácora de Alta Gerencia */}
                    <div className="bg-slate-800 flex flex-col p-6 rounded-2xl border border-amber-500/30 shadow-xl h-full min-h-[300px]">
                        <h4 className="text-lg font-bold text-amber-400 mb-4 flex items-center gap-2 uppercase tracking-wider">
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></span>
                            Bitácora de Alta Gerencia
                        </h4>
                        <div className="flex-1 overflow-y-auto space-y-2 pr-2 mb-4 max-h-[250px]">
                            {notasGerencia.length === 0 ? (
                                <p className="text-xs text-slate-500 italic text-center py-4">No hay registros estratégicos aún.</p>
                            ) : (
                                notasGerencia.map(nota => (
                                    <div key={nota.id} className={`p-3 rounded-lg border shadow-sm flex flex-col gap-1 ${nota.esJunta ? 'bg-indigo-500/10 border-indigo-500/30' : 'bg-slate-900/60 border-amber-500/20'}`}>
                                        <div className="flex justify-between items-center text-[10px]">
                                            <div className="flex items-center gap-2">
                                                <span className={`font-bold ${nota.esJunta ? 'text-indigo-400' : 'text-amber-400/90'}`}>{nota.autor}</span>
                                                {nota.esJunta && (
                                                    <span className="flex items-center gap-1 text-[9px] bg-indigo-500/20 text-indigo-300 px-1.5 py-0.5 rounded font-bold uppercase">
                                                        <Users size={10} /> Acuerdo de Junta
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-500 font-mono">
                                                    {new Date(nota.fecha).toLocaleDateString()}
                                                </span>
                                                {currentUser?.role === 'GERENTE' && (
                                                    <button onClick={() => handleEditarNotaGerencia(nota)} className="text-slate-400 hover:text-amber-400 transition-colors">
                                                        <Edit size={12} />
                                                    </button>
                                                )}
                                                {currentUser?.role === 'ADMIN' && (
                                                    <button onClick={() => handleEliminarNotaGerencia(nota.id)} className="text-slate-400 hover:text-red-400 transition-colors">
                                                        <Trash2 size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-xs text-slate-200 leading-relaxed font-sans">{nota.mensaje}</p>
                                    </div>
                                ))
                            )}
                        </div>
                        <form onSubmit={handleSaveNotaGerencia} className="flex flex-col gap-2">
                            <div className="flex gap-2">
                                <input 
                                    type="text" 
                                    value={nuevaNotaGerencia} 
                                    onChange={(e) => setNuevaNotaGerencia(e.target.value)} 
                                    placeholder="Instrucción estratégica..." 
                                    className="flex-1 bg-slate-900 border border-slate-700/60 rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-amber-500/50 font-bold" 
                                />
                                <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-slate-950 font-bold px-4 py-2 rounded-lg text-xs transition-colors flex items-center justify-center gap-1 shadow">
                                    <Send size={14} />
                                </button>
                            </div>
                            <label className="flex items-center gap-2 text-slate-400 text-xs cursor-pointer hover:text-indigo-400 transition-colors select-none font-medium ml-1">
                                <input 
                                    type="checkbox" 
                                    checked={esAcuerdoJunta} 
                                    onChange={(e) => setEsAcuerdoJunta(e.target.checked)} 
                                    className="rounded bg-slate-900 border-slate-700 text-indigo-500 focus:ring-0 w-3.5 h-3.5" 
                                />
                                Marcar como "Acuerdo de Junta"
                            </label>
                        </form>
                    </div>
                </div>
            )}

            {/* MOTOR DE RUTINAS DIARIAS (ATC / GC) */}
            {(isATC || isGC) && misRutinasDiarias.length > 0 && (
                <div className={`p-6 rounded-2xl border backdrop-blur-md shadow-lg ${isGC ? 'bg-cyan-500/5 border-cyan-500/20' : 'bg-emerald-500/5 border-emerald-500/20'} animate-in fade-in duration-300`}>
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                        <div>
                            <h2 className={`text-xl font-bold flex items-center gap-2 ${isGC ? 'text-cyan-400' : 'text-emerald-400'}`}>
                                <CheckSquare size={24} /> Mi Rutina Diaria
                            </h2>
                            <p className="text-slate-400 text-xs mt-1">Completa tus responsabilidades obligatorias del día</p>
                        </div>
                        {/* Progreso */}
                        <div className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/50 px-4 py-2 rounded-xl">
                            <span className="text-slate-400 text-xs font-semibold">Progreso:</span>
                            <span className={`font-mono font-bold text-sm ${isGC ? 'text-cyan-400' : 'text-emerald-400'}`}>
                                {misRutinasDiarias.filter(r => r.completado).length} / {misRutinasDiarias.length}
                            </span>
                            <div className="w-24 bg-slate-700 rounded-full h-2 overflow-hidden">
                                <div 
                                    className={`h-2 rounded-full transition-all duration-500 ${isGC ? 'bg-cyan-500' : 'bg-emerald-500'}`} 
                                    style={{ width: `${(misRutinasDiarias.filter(r => r.completado).length / misRutinasDiarias.length) * 100}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                        {misRutinasDiarias.map(r => (
                            <label 
                                key={r.id} 
                                className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer select-none transition-all border
                                    ${r.completado 
                                        ? 'bg-slate-800/20 border-slate-700/30 text-slate-500 line-through' 
                                        : isGC 
                                            ? 'bg-slate-800 border-slate-700 hover:border-cyan-500/50 text-slate-200' 
                                            : 'bg-slate-800 border-slate-700 hover:border-emerald-500/50 text-slate-200'
                                    }`}
                            >
                                <input 
                                    type="checkbox" 
                                    checked={r.completado} 
                                    onChange={() => handleToggleRutina(r.id)} 
                                    className={`mt-0.5 rounded border-slate-600 focus:ring-0 focus:ring-offset-0 ${isGC ? 'text-cyan-500 bg-slate-900' : 'text-emerald-500 bg-slate-900'}`} 
                                />
                                <span className="text-xs font-medium leading-tight">{r.texto}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}

            {/* COMPONENTE FLOTANTE: GASTOS POR REVISAR (ADMIN) */}
            {gastosPorRevisar.length > 0 && (
                <div className="fixed bottom-6 right-6 z-50 w-80 bg-slate-800 rounded-2xl border border-amber-500/30 shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
                    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-3 flex justify-between items-center">
                        <h4 className="text-sm font-bold text-amber-400 flex items-center gap-2">
                            <AlertTriangle size={16} /> Gastos por Revisar ({gastosPorRevisar.length})
                        </h4>
                    </div>
                    <div className="p-3 max-h-60 overflow-y-auto space-y-2">
                        {gastosPorRevisar.map(g => (
                            <div key={g.id} className="p-3 bg-slate-900/60 rounded-xl border border-slate-700/50 flex flex-col gap-1.5">
                                <div className="flex justify-between items-start">
                                    <span className="text-xs font-bold text-white truncate max-w-[150px]">{g.descripcion}</span>
                                    <span className="text-amber-400 font-mono font-bold text-xs">${Number(g.amount || g.monto || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center text-[10px] text-slate-400">
                                    <span>Por: {g.registrado_por || 'Anónimo'}</span>
                                    <span className="bg-slate-800 px-1.5 py-0.5 rounded border border-slate-700">{g.metodo_pago}</span>
                                </div>
                                <button onClick={() => handleConfirmarGasto(g.id)} className="mt-1 w-full bg-amber-600 hover:bg-amber-500 text-white text-[11px] font-bold py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1">
                                    <CheckCircle size={12} /> Confirmar / Revisado
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* BANNER NOTIFICACIONES BITÁCORA */}
            {canSeeMuro && pendientesVerificacion > 0 && (
                <div onClick={() => navigate('/inventario')} className="bg-amber-500/20 border border-amber-500/50 rounded-xl p-4 mb-6 flex items-center justify-between cursor-pointer hover:bg-amber-500/30 transition-colors">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="text-amber-500" size={24} />
                        <div>
                            <h4 className="text-amber-500 font-bold">¡Atención Requerida!</h4>
                            <p className="text-amber-200 text-sm">Tienes {pendientesVerificacion} actividades o renovaciones pendientes de verificar en el Almacén.</p>
                        </div>
                    </div>
                    <span className="text-amber-500 text-sm font-bold bg-amber-500/20 px-3 py-1 rounded-full">Revisar &rarr;</span>
                </div>
            )}

            {/* SECCIÓN FINANCIERA (SOLO ADMIN/GERENTE) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Tarjeta 1: Usuarios (Clientes Finales) */}
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Clientes Finales</span>
                        <div className="w-8 h-8 rounded bg-emerald-500/20 text-emerald-500 flex items-center justify-center">
                            <Users size={18} />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-white mb-1">
                        {loading ? '...' : data.totalUsers}
                    </div>
                    <div className="text-xs text-emerald-400 flex items-center gap-1">
                        <TrendingUp size={12} /> <span>+0% vs mes anterior</span>
                    </div>
                </div>

                {/* Tarjeta 2: Ingresos */}
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Ingresos del Mes</span>
                        <div className="w-8 h-8 rounded bg-emerald-500/20 text-emerald-500 flex items-center justify-center">
                            <DollarSign size={18} />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-white mb-1">
                        {loading ? '...' : `$${data.ingresosMes.toLocaleString()}`}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                        <span>Datos de movimientos</span>
                    </div>
                </div>

                {/* Tarjeta 3: Gastos */}
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Gastos del Mes</span>
                        <div className="w-8 h-8 rounded bg-red-500/20 text-red-500 flex items-center justify-center">
                            <CreditCard size={18} />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-white mb-1">
                        {loading ? '...' : `$${data.gastosMes.toLocaleString()}`}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                        <span>Datos de movimientos</span>
                    </div>
                </div>

                {/* Tarjeta 4: Utilidad Neta */}
                <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 shadow-lg">
                    <div className="flex justify-between items-start mb-4">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Utilidad Neta</span>
                        <div className="w-8 h-8 rounded bg-blue-500/20 text-blue-500 flex items-center justify-center">
                            <TrendingUp size={18} />
                        </div>
                    </div>
                    <div className="text-2xl font-bold text-white mb-1">
                        {loading ? '...' : `$${data.utilidadNeta.toLocaleString()}`}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                        <span>Ingresos - Gastos</span>
                    </div>
                </div>
            </div>

            {/* Gráfico y Progreso Operativo */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {/* Gráfico */}
                <div className="bg-slate-800 rounded-lg p-5 border border-slate-700 shadow-lg lg:col-span-2">
                    <div className="flex flex-wrap justify-between items-center mb-4">
                        <h3 className="text-white font-bold">Evolución Financiera ({new Date().getFullYear()})</h3>
                    </div>
                    <div className="h-[300px]">
                        {data.tableData ? (
                            <>
                                <ResponsiveContainer width="100%" height="100%">
                                    <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                                        <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                                        <YAxis yAxisId="left" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(value) => `$${value}`} />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                                        <Bar yAxisId="left" dataKey="Ingresos" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                                        <Line yAxisId="left" type="monotone" dataKey="Gastos" stroke="#f43f5e" strokeWidth={3} name="Gastos Totales" dot={{ r: 4, fill: '#f43f5e', strokeWidth: 2, stroke: '#1e293b' }} />
                                        <Line yAxisId="left" type="monotone" dataKey="Utilidad" stroke="#34d399" strokeWidth={3} name="Utilidad Neta" dot={{ r: 4, fill: '#34d399', strokeWidth: 2, stroke: '#1e293b' }} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </>
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500 font-medium">Cargando datos...</div>
                        )}
                    </div>
                </div>

                {/* Columna 2: Muro de Supervisión */}
                <div className="lg:col-span-1">
                    {canSeeMuro && (
                        <div className="bg-slate-800 rounded-lg border border-slate-700 p-5 shadow-lg h-full flex flex-col justify-between">
                            <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
                                <MessageSquare size={16} className="text-indigo-400" /> Muro de Supervisión
                            </h4>
                            <div className="flex-1 overflow-y-auto space-y-2 pr-2 mb-3 max-h-[300px]">
                                {notas.length === 0 ? (
                                    <p className="text-xs text-slate-500 italic">No hay notas registradas.</p>
                                ) : (
                                    notas.map(nota => (
                                        <div key={nota.id} className="bg-slate-900/50 p-2 rounded-lg border border-slate-700/50">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-[10px] font-bold text-emerald-400">{nota.autor}</span>
                                                <span className="text-[9px] text-slate-500">{new Date(nota.fecha).toLocaleTimeString()}</span>
                                            </div>
                                            <p className="text-xs text-slate-300">{nota.mensaje}</p>
                                        </div>
                                    ))
                                )}
                            </div>
                            <form onSubmit={handleAddNota} className="relative mt-auto">
                                <input 
                                    type="text" 
                                    value={nuevaNota}
                                    onChange={e => setNuevaNota(e.target.value)}
                                    placeholder="Escribe una nota..." 
                                    className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-3 pr-8 py-2 text-xs text-white focus:border-indigo-500 focus:outline-none"
                                />
                                <button type="submit" disabled={!nuevaNota.trim()} className="absolute right-2 top-1/2 -translate-y-1/2 text-indigo-400 hover:text-indigo-300 disabled:opacity-50">
                                    <Send size={14} />
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>


            {/* 3. Tabla Financiera Detallada */}
            {data.tableData && (
                <div className="bg-slate-800 rounded-lg border border-slate-700 overflow-hidden">
                    <div className="p-4 border-b border-slate-700">
                        <h3 className="text-white font-bold">Resumen Financiero Detallado ({new Date().getFullYear()})</h3>
                    </div>
                    <div className="overflow-x-auto whitespace-nowrap">
                        <table className="w-full text-sm text-left text-slate-300">
                            <thead className="bg-slate-900 text-slate-400 uppercase text-xs">
                                <tr>
                                    <th className="px-6 py-3">Concepto / Categoría</th>
                                    <th className="px-4 py-3">Ene</th><th className="px-4 py-3">Feb</th><th className="px-4 py-3">Mar</th>
                                    <th className="px-4 py-3">Abr</th><th className="px-4 py-3">May</th><th className="px-4 py-3">Jun</th>
                                    <th className="px-4 py-3">Jul</th><th className="px-4 py-3">Ago</th><th className="px-4 py-3">Sep</th>
                                    <th className="px-4 py-3">Oct</th><th className="px-4 py-3">Nov</th><th className="px-4 py-3">Dic</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* INGRESOS */}
                                <tr className="bg-slate-900/50">
                                    <td colSpan="13" className="px-6 py-2 font-bold text-emerald-500 uppercase tracking-wider">INGRESOS</td>
                                </tr>
                                {[FINANCIAL_INCOME_KEY].map(cat => (
                                    <tr key={cat} className="border-b border-slate-700 hover:bg-slate-750">
                                        <td className="px-6 py-3 text-slate-300">{cat}</td>
                                        {data.tableData.ingresos[cat].map((val, idx) => (
                                            <td key={idx} className="px-4 py-3 text-slate-300">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                        ))}
                                    </tr>
                                ))}
                                <tr className="bg-slate-700/30 font-bold border-b-2 border-slate-600">
                                    <td className="px-6 py-3 text-white">TOTAL INGRESOS</td>
                                    {data.tableData.totalIngresos.map((val, idx) => (
                                        <td key={idx} className="px-4 py-3 text-emerald-400">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                    ))}
                                </tr>

                                {/* GASTOS */}
                                <tr className="bg-slate-900/50">
                                    <td colSpan="13" className="px-6 py-2 font-bold text-red-500 uppercase tracking-wider">GASTOS</td>
                                </tr>
                                {FINANCIAL_EXPENSE_KEYS.map(cat => (
                                    <tr key={cat} className="border-b border-slate-700 hover:bg-slate-750">
                                        <td className="px-6 py-3 text-slate-300">{cat}</td>
                                        {(data.tableData.gastos[cat] || Array(12).fill(0)).map((val, idx) => (
                                            <td key={idx} className="px-4 py-3 text-slate-300">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                        ))}
                                    </tr>
                                ))}
                                <tr className="bg-slate-700/30 font-bold border-b-2 border-slate-600">
                                    <td className="px-6 py-3 text-white">TOTAL GASTOS</td>
                                    {data.tableData.totalGastos.map((val, idx) => (
                                        <td key={idx} className="px-4 py-3 text-red-400">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                    ))}
                                </tr>

                                {/* RESULTADOS */}
                                <tr className="bg-slate-900/50">
                                    <td colSpan="13" className="px-6 py-2 font-bold text-blue-500 uppercase tracking-wider">RESULTADOS</td>
                                </tr>
                                <tr className="bg-cyan-900/20 font-bold border-y border-cyan-800">
                                    <td className="px-6 py-3 text-cyan-400">FONDO DE RESERVA (5%)</td>
                                    {data.tableData.fondoReserva?.map((val, i) => (
                                        <td key={i} className="px-4 py-3 text-cyan-400">
                                            ${(val || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                        </td>
                                    ))}
                                </tr>
        <tr className="bg-fuchsia-900/20 font-bold border-b border-fuchsia-800">
            <td className="px-6 py-3 text-fuchsia-400">FONDO DE PUBLICIDAD (2%)</td>
            {(data.tableData.fondoPublicidad || Array(12).fill(0)).map((val, i) => (
                <td key={i} className="px-4 py-3 text-fuchsia-400">${(val || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
            ))}
        </tr>

        <tr className="bg-emerald-900/20 font-bold border-b border-emerald-800">
            <td className="px-6 py-3 text-emerald-400">UTILIDAD NETA</td>
            {(data.tableData.utilidadNeta || Array(12).fill(0)).map((val, i) => (
                <td key={i} className="px-4 py-3 text-emerald-400">${(val || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
            ))}
        </tr>


                                <tr className="bg-emerald-900/20 font-bold border-b border-emerald-800">
                                    <td className="px-6 py-3 text-emerald-400">GANANCIA A REPARTIR</td>
                                    {data.tableData.totalIngresos.map((_, i) => {
                                        const utMes = data.tableData.totalIngresos[i] - data.tableData.totalGastos[i];
                                        const fondo = data.tableData.fondoReserva?.[i] || 0;
                                        const ganancia = utMes > 0 ? utMes - fondo : utMes;
                                        return (
                                        <td key={i} className="px-4 py-3 text-emerald-400">
                                            ${ganancia.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                        </td>
                                    )})}
                                </tr>

                                {/* RETIROS SOCIOS */}
                                <tr className="bg-slate-900/50">
                                    <td colSpan="13" className="px-6 py-2 font-bold text-amber-500 uppercase tracking-wider">DISTRIBUCIÓN SOCIOS</td>
                                </tr>
                                {FINANCIAL_PARTNER_KEYS.map(socio => (
                                    <tr key={socio} className="bg-blue-900/10 border-b border-slate-700 hover:bg-slate-750">
                                        <td className="px-6 py-3 text-blue-300 italic">{socio}</td>
                                        {data.tableData.retiros[socio].map((val, idx) => (
                                            <td key={idx} className="px-4 py-3 text-blue-300 italic">${val.toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            {/* RENDERIZAR PANEL DE KPIs */}
            {renderPanelKPIs()}
            
            {/* RENDERIZAR ESTADO DE EQUIPO AL FINAL PARA ADMIN */}
            {renderEstadoEquipo()}
        </div>
    );
};

export default Dashboard;
